import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ip_back } from '../config.js';
import { useBrand } from '../context/BrandContext';
import { useWizardViewportHeight } from '../hooks/useWizardViewportHeight';
import { syncBrandAfterAuth } from '../utils/brandAuth';
import { LAST_BRAND_CLUB_ID_KEY } from '../utils/localStorageBrand';
import { resolveClubImageUrl } from '../utils/resolveClubImageUrl';
import { applyInvitePageLanguage, markInviteUiLangOverride } from '../utils/clubCountryLanguage';
import { applyAppLanguage } from '../utils/appLanguages';
import { parseDateOfBirth } from '../utils/parseDateOfBirth';
import { LanguageShortcutFab } from './LanguageShortcutFab';
import {
  PHASES,
  STEP_PROMPT_KEY,
  STEP_TO_DIMENSION,
  COMPETITION_YEARS_OPTIONS,
  DEFAULT_TECHNICAL_SPRINT_WEEKS,
  createInitialState,
  transition,
  slotsToFormData,
  getDimensionMeta,
  buildSmartSnapshot,
  isLuxembourgClassementContext,
  DigipingSmartSnapshot,
  DigipingMark
} from '../digiping';
import {
  fetchSmartProfile
} from './funtionsBD/digipingSmartService';
import {
  persistWizardDraft,
  clearWizardDraft,
  loadWizardDraftLocal,
  extractResumeDraft
} from '../digiping/wizardDraftPersistence';
import {
  focusCategoryIdsToLoad,
  visibleFocusAttributes,
  PURPOSE_RECREATIONAL,
  PURPOSE_HEALTH
} from '../constants/profilingFocusCatalog';
import {
  COMPETITION_OBJECTIVE_KINDS,
  composeFinalObjectiveLabel
} from '../constants/finalObjective';
import {
  leisureDisplayLabel,
  leisureExperienceLabel,
  resolveLeisureSmartPreset,
  isLeisurePurpose,
  LEISURE_BEGINNER_VISIBLE_FOCUS_IDS
} from '../constants/leisureSmartPresets';
import {
  resolveHealthSmartPreset,
  healthExperienceLabel,
  isHealthPurpose
} from '../constants/healthSmartPresets';
import { formatFrenchSentenceCase } from '../utils/frenchSentenceCase';
import {
  HEALTH_DIMENSIONS,
  HEALTH_WHO_SELF_CHECK,
  HEALTH_SESSIONS,
  healthFocusIdsForDimensions,
  whoBandFromUserDob
} from '../constants/healthPlayerProfiling';
import { FOCUS_OTHER_ATTRIBUTE_ID } from '../constants/focusAttributes';
import ScforhApproachIntro from './ScforhApproachIntro';
import ScforhWhoCapsule from './ScforhWhoCapsule';
import {
  competitionExpectedOptions,
  buildExpectedResultLabel,
} from '../constants/competitionExpectedResults';
import './AIProfilingWizard.css';

// Material Icons
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StarsIcon from '@mui/icons-material/Stars';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import SecurityIcon from '@mui/icons-material/Security';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import GroupsIcon from '@mui/icons-material/Groups';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TableTennisRacketIcon from './TableTennisRacketIcon';

const EMPHASIS_TONE_MAP = [
  { re: /^(specific|spécifique|específico|spezifisch)/i, tone: 's' },
  { re: /^(measurable|mesurable|medible|messbar)/i, tone: 'm' },
  { re: /^(achievable|atteignable|alcanzable|erreichbar)/i, tone: 'a' },
  { re: /^(relevant|réaliste|relevante)/i, tone: 'r' },
  { re: /^(time-?bound|temporel|temporal|terminiert)/i, tone: 't' },
  { re: /^smart$/i, tone: 'smart' },
  { re: /^(competition|compétition|competición|wettkampf)/i, tone: 'goal' },
  { re: /^(recreational|loisir|recreativo|freizeit)/i, tone: 'goal' },
  { re: /^(health|santé|salud|gesundheit)/i, tone: 'goal' }
];

const emphasisTone = (word) => {
  const hit = EMPHASIS_TONE_MAP.find(({ re }) => re.test(String(word || '').trim()));
  return hit ? hit.tone : 'default';
};

/** Turn **labels** / bullet lines into styled nodes (no raw asterisks). */
const renderRichNodes = (text) => {
  if (!text) return null;
  const lines = String(text).split('\n');
  return lines.map((line, lineIdx) => {
    const bulletMatch = line.match(/^\*\s+(.*)$/);
    const content = bulletMatch ? bulletMatch[1] : line;
    const nodes = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0;
    let match;
    let key = 0;
    while ((match = re.exec(content)) !== null) {
      if (match.index > last) {
        nodes.push(<span key={`t${key++}`}>{content.slice(last, match.index)}</span>);
      }
      const label = match[1];
      nodes.push(
        <span key={`e${key++}`} className={`dp-em dp-em--${emphasisTone(label)}`}>
          {label}
        </span>
      );
      last = match.index + match[0].length;
    }
    if (last < content.length) {
      nodes.push(<span key={`t${key++}`}>{content.slice(last)}</span>);
    }
    if (!content && !bulletMatch) {
      return <br key={`br${lineIdx}`} />;
    }
    if (bulletMatch) {
      return (
        <div key={`li${lineIdx}`} className="dp-rich-bullet">
          {nodes}
        </div>
      );
    }
    return (
      <span key={`ln${lineIdx}`} className="dp-rich-line">
        {nodes}
        {lineIdx < lines.length - 1 ? <br /> : null}
      </span>
    );
  });
};

const RichText = ({ text, className = '' }) => (
  <div className={`dp-rich-text ${className}`.trim()}>{renderRichNodes(text)}</div>
);

/** Visible plain text while streaming — hides markdown markers. */
const visiblePlainSlice = (text, count) => {
  const slice = String(text || '').slice(0, count);
  return slice
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/\*\*[^*]*$/, (m) => m.replace(/\*\*/g, ''))
    .replace(/^\*\s+/gm, '• ');
};

/** Slice that keeps markdown renderable so bold / SMART tones appear while typing. */
const safeMarkdownSlice = (text, count) => {
  let slice = String(text || '').slice(0, Math.max(0, count));
  const markers = slice.match(/\*\*/g);
  if (markers && markers.length % 2 === 1) {
    slice += '**';
  }
  return slice;
};

// Custom Self-Contained Localizations
const TRANSLATIONS = {
  en: {
    coachName: 'Pingflo',
    typing: 'Pingflo is typing...',
    introTitle: 'SMART Training Tool',
    introText: 'Meet **Pingflo** — host of your club\'s SMART agents team (Pingwi, Pingpro, Pingmynd). Together we\'ll build your athlete profile. To progress twice as fast, we use the **SMART** goal methodology:',
    leisureIntroTitle: 'Challenges & games',
    leisureIntroText: 'Meet **Pingflo** — with **Pingwi** for playful challenges. We\'ll figure out where you are and pick **fun challenges** so learning feels like a game.',
    digipingTag: 'Your Club SMART Agent',
    smartItems: [
      { letter: 'S', label: 'Specific', desc: 'Name the precise training targets that matter most — a ★ primary focus and optional · secondary ones — so every session has a clear, concrete aim toward your season result.' },
      { letter: 'M', label: 'Measurable', desc: 'Player progress is tracked through subjective and objective evaluations: self-assessments, coach follow-up, and exercise metrics from training analysis.' },
      { letter: 'A', label: 'Achievable', desc: 'Your coach evaluates your training efforts against the expected results — to judge whether progress is realistic and on track.' },
      { letter: 'R', label: 'Relevant', desc: 'SMART training objectives must be relevant — they must matter to you and fit your wider plans. Your coach validates them and aligns training toward the expected results.' },
      { letter: 'T', label: 'Time-bound', desc: 'Two clocks: a 3-week sprint for personalized exercises on your primary and secondary SMART objectives, and a half-season horizon for presence toward your season result.' }
    ],
    leisureSmartItems: [
      { letter: '🎮', label: 'Have fun', desc: 'Play for the joy of the game — no pressure, no jargon.' },
      { letter: '🎯', label: 'Learn by playing', desc: 'Small challenges teach the basics without a heavy questionnaire.' },
      { letter: '⭐', label: 'Challenges', desc: 'Unlock simple quests and easy drills tailored to your age and level.' }
    ],
    introAction: "Let's align your mindset! Press below to start:",
    leisureIntroAction: 'Ready for your first challenge? Press below:',
    startBtn: 'Begin SMART Profiling',
    leisureStartBtn: 'Start the fun path',
    healthSmartItems: [
      { letter: 'S', label: 'Specific', desc: 'Grow physical, mental, and/or social well-being through club table tennis.' },
      { letter: 'M', label: 'Measurable', desc: 'WHO minutes for your age plus the club sessions you can keep. Club TT counts toward the total.' },
      { letter: 'A', label: 'Achievable', desc: 'You will have training milestones that support your physical and mental objectives.' },
      { letter: 'R', label: 'Relevant', desc: 'Your coach will make sure the plan is relevant for your objectives.' },
      { letter: 'T', label: 'Time-bound', desc: 'A season or 4-month phase, then review with the club.' }
    ],
    continueBtn: 'Continue SMART setup',
    resumeWelcome: 'Welcome back — continuing where you left off.',
    resumeHint: 'You have saved progress from a previous session.',
    joinClubBanner: 'Join {club}',
    editAnswer: 'Edit',
    editingHint: 'Update your answer below',
    back: 'Back',
    confirm: 'Confirm',
    selectOption: 'Select an option:',
    enterToContinue: 'Press',
    searchCountry: 'Search country...',
    searchClub: 'Search club...',
    enterRank: 'Enter your classement/ranking level...',
    dobError: 'Please enter a valid date of birth (DD/MM/YYYY)',
    emailError: 'Please enter a valid email address',
    emailExistsHint: 'This email is already registered. Enter your password to update your profile and SMART path.',
    passError: 'Password must be at least 6 characters',
    passMatchError: 'Passwords do not match',
    passWrongError: 'Incorrect password. Please try again.',
    passCreatePlaceholder: 'Create Password',
    passConfirmPlaceholder: 'Confirm Password',
    passExistingPlaceholder: 'Your password',
    saving: 'Registering & saving profile...',
    savingUpdate: 'Signing in and updating your profile & SMART path...',
    success: 'Registration complete!',
    successUpdate: 'Profile and SMART path updated!',
    redirecting: 'Setting up your customized sports center...',
    emailVerifyHint: 'We sent a confirmation link to your email. Confirm it before joining a training session — Home and Profile are available now.',
    // Conversation prompts
    qPurpose: 'Great! First, what is your primary goal or motivation in this sport?',
    qExperience: 'How would you describe your overall playing experience?',
    qCompetitionYears: 'How many years have you been playing in competition?',
    qStyle: 'What is your preferred playing style on the table?',
    competitionYearsLt1: 'Less than 1 year',
    competitionYears1_2: '1–2 years',
    competitionYears3_5: '3–5 years',
    competitionYears5_10: '5–10 years',
    competitionYears10plus: 'More than 10 years',
    qPrimaryFocus: 'What is the primary training focus you want to target right now?',
    qPrimaryFocusCompetition: 'What do you think you need to train more to get closer to those results?',
    qSecondaryFocus: 'Are there any secondary focuses you want to add? You can select up to 2 options:',
    qSecondaryFocusCompetition: 'What are the 2 other aspects of your game where you feel less confident?',
    qCountry: 'Which country do you represent in competition?',
    qClub: 'Which club do you represent?',
    qLevel: 'What is your current official FLTT classification level?',
    qLevelGeneric: 'What is your current official classification level?',
    qCustomLevel: 'What is your current classification level in your country?',
    qGrip: 'What racket grip style do you use?',
    qHand: 'Which is your playing hand?',
    qMetric: 'What near-term number will you track to measure progress? Pick a metric and set a target.',
    qTrainingVolumeCompetition: 'How many times per week will you train to reach your competition goals? Enter sessions and hours so Pingflo can track your progress.',
    qTrainingVolumeRecreational: 'How many times a week do you come to have fun at the club? Enter sessions and hours so Pingflo can cheer you on.',
    qTrainingVolumeHealth: 'How many club sessions per week can you keep? Club table tennis counts toward the WHO total — not a table-tennis-only quota.',
    qHealthIntro: 'Health in a sports club is physical, mental, and social well-being. Table tennis is a natural Sports Club for Health setting — we weave it into club sessions, not a side programme.',
    qHealthDimensions: 'Which dimensions should grow for you? Select all that apply.',
    qHealthWhoCheck: 'Here is the WHO activity guideline for your age. Club sessions count toward it. Where are you today?',
    qHealthDelivery: 'Your coach will make sure the plan is relevant for your objectives.',
    qHealthIntensity: 'You will have training milestones that support your physical and mental objectives.',
    qHealthSnapshot: 'Here is your club health SMART plan. Confirm to save it.',
    healthDim_physical: 'Physical',
    healthDim_mental: 'Mental',
    healthDim_social: 'Social',
    healthWho_below: 'Below the guideline',
    healthWho_around: 'Around the guideline',
    healthWho_above: 'I already meet it',
    healthDelivery_into_coaching: 'In regular club training',
    healthDelivery_separate_sessions: 'Dedicated health-enhancing sessions',
    healthDelivery_both: 'Both',
    healthIntensity_regular: 'Regular club intensity',
    healthIntensity_gentle: 'Gentle / low-impact',
    healthIntensity_adapted: 'Adapted play — coach should know',
    healthSessions_1: '1 session a week',
    healthSessions_2: '2 sessions a week',
    'healthSessions_3+': '3 or more sessions a week',
    healthSnapshotGoal: 'Specific',
    healthSnapshotTrack: 'Measurable',
    healthSnapshotDoable: 'Achievable',
    healthSnapshotWhy: 'Relevant',
    healthSnapshotWhen: 'Time-bound',
    healthSnapshotConfirm: 'This is my health plan',
    healthContinue: 'Continue',
    healthIntroTitle: 'Health in your club',
    healthIntroText: 'Meet **Pingflo**. We will turn your health answers into SMART training objectives for table tennis at the club — physical, mental, and social well-being in daily sessions.',
    healthIntroAction: 'Ready to set your health path?',
    healthStartBtn: 'Begin health profiling',
    qTrainingVolume: 'How many sessions per week will you train, and how many hours per session? Pingflo will use this to track your progress.',
    sessionsPerWeekLabel: 'Sessions per week',
    hoursPerWeekLabel: 'Hours per week',
    hoursPerSessionLabel: 'Hours per session',
    sessionsUnit: 'sessions/week',
    hoursUnit: 'h/session',
    trainingVolumeHealthOk: 'Nice work! Around {hours} h/week (~{minutes} min) meets or beats the adult WHO guideline of 150 minutes of activity per week. Under 18, WHO recommends 60 minutes every day — club sessions count toward that. Pingflo will keep tracking your progress — keep it up!',
    trainingVolumeHealthLow: 'Got it — about {hours} h/week (~{minutes} min). Adults: at least 150 minutes of activity per week. Under 18: 60 minutes every day. Club sessions count toward those totals — consider walking, cycling, or extra sessions. Pingflo will help you track it.',
    qMilestones: 'Which mid- or long-term milestones will advance your strategy? Pick up to 2 — your coach will validate these and may redefine or better target them.',
    qFinalObjective: 'What season result are you aiming for? This is the destination — next you will pick SMART ★ primary and · secondary objectives that build the path.',
    qRelevance: 'Your SMART training objectives should be relevant — they must matter to you and fit your wider plans. Your coach will validate them and align training to help you reach the expected results.',
    qRelevanceCompetition: 'Your SMART training objectives should be relevant — they must matter to you and fit your wider plans. Your coach will validate them and align training to help you reach the expected results.',
    relevanceContinue: 'Continue',
    relevanceAckLabel: 'Coach will validate and align training',
    finalObjectiveHint: 'Pick one — your coach can refine the target later.',
    finalObjectiveKindClassement: 'Climb ranking',
    finalObjectiveKindTeam: 'Win / promote in the team championship',
    finalObjectiveKindTournament: 'Win a tournament',
    finalObjectiveKindOther: 'Other',
    finalObjectiveOtherPlaceholder: 'Describe your competition objective…',
    qGender: 'What is your gender?',
    qDob: 'What is your date of birth? (Please use DD/MM/YYYY format)',
    qFirstName: 'What is your first name? We use it to check if you are already on this club’s list.',
    qLastName: 'What is your last name?',
    qName: 'What is your first name? We use it to check if you are already on this club’s list.',
    phFirstName: 'First name',
    phLastName: 'Last name',
    qEmail: 'What email address would you like to use for your account?',
    qPassword: 'Finally, please create a secure password (minimum 6 characters):',
    qPasswordExisting: 'This email is already in the system. Enter your password to update your profile and SMART path:',
    qCycleLength: 'Personalized exercises on your ★ primary and · secondary SMART objectives run on a **3-week** sprint by default (last week = review). Your **season result** (classement, team, tournament) is judged on a **half-season** presence clock. You can ask to change the sprint length later **with your coach’s agreement**.',
    qCycleLengthLeisure: 'Your fun path runs in a **3-week** cycle. Confirm to continue — challenges come later.',
    cycleWeeks3: '3-week technical sprint (default)',
    cycleWeeks4: '4-week technical sprint',
    cycleWeeksHint: 'Default 3 weeks · change later with coach agreement',
    cycleWeeksConfirm: 'Got it — 3-week technical sprint',
    cycleWeeksLeisureHint: '3 weeks for this cycle',
    cycleWeeksLeisureConfirm: 'Got it — 3-week cycle',
    metricTargetPlaceholder: 'Target number…',
    metricCustomLabel: 'Custom metric',
    milestonesHint: 'Coach may refine milestones and challenges',
    resourceNotePlaceholder: 'Skills / time / equipment note (optional)',
    horizonMid: 'Mid-term',
    horizonLong: 'Long-term',
    relevanceCustomPlaceholder: 'Or write why it matters…',
    coachPendingCaption: 'Coach',
    coachPendingNote: 'Awaiting coach agreement (milestones + relevance)',
    coachMayRefine: 'Coach may refine',
    achievableSummary: 'Coach evaluates your training efforts against the expected results',
    coachBanner: 'Continue when you are ready — your coach will lock in the path with you.',
    
    // Mindset assessments based on main purpose
    assessmentTitle: 'Your Club SMART Agent',
    leisureAssessmentTitle: 'Your challenge',
    snapshotEmpty: '—',
    cycleLabel: 'T-short: 3-week technical sprint (default · change with coach) · T-long: half-season for competition',
    competitionMindset: 'Outstanding! Since your focus is **Competition**, your mindset must be built on high tactical consistency and professional preparation. To execute a SMART sports approach: \n\n* **Specific**: Your **season result** (classement, team, tournament) is the destination. ★ primary and · secondary SMART objectives (footwork, strokes, …) are the path. Personalized exercises for those objectives come after technical profiling, on a 3-week cycle.\n* **Measurable**: Subjective autoeval plus objective training metrics on those exercises, week by week.\n* **Achievable**: Your coach evaluates your training efforts against the expected results — whether progress is realistic and on track.\n* **Relevant**: The goal must matter and fit with your wider plans and objectives.\n* **Time-bound**: Exercises on ★ and · objectives run in a **~3-week** sprint; presence toward the season result is judged over a **half-season**.',
    recreationalMindset: 'You\'re here to **have fun**, **relax**, and **learn a few basics**. Pingflo will feed you **challenges & games** around beginner skills:\n\n* **Your challenge** — a simple quest you can enjoy at the club\n* **How you track it** — sessions and a fun rating after each visit\n* **What\'s next** — unlock the next challenge when you\'re ready',
    qPrimaryFocusLeisure: 'What sounds most fun to you right now?',
    qFunChallenge: 'Here\'s your fun challenge — ready to go?',
    funChallengeConfirm: 'Let\'s go!',
    funChallengeGoal: 'Your goal',
    funChallengeTrack: 'How you\'ll track progress',
    funChallengeMilestone: 'Your challenge',
    funChallengeCycle: 'Your calendar',
    healthMindset: 'Your club table tennis sessions now count toward your health plan. Pingflo will remind you of the WHO guideline without turning it into homework. SMART here is a season or 4-month phase — physical, mental, and social well-being woven into club sessions, not a 3-week technical sprint.',
    metricOptions: {
      1: [
        { id: 'hr_minutes', kind: 'hr_minutes', label: 'Weekly HR-zone minutes', unit: 'min' },
        { id: 'streak_weeks', kind: 'streak_weeks', label: 'Consistency streak', unit: 'weeks' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Active minutes per session', unit: 'min' }
      ],
      2: [
        { id: 'rally_count', kind: 'rally_count', label: 'Consecutive rally count', unit: 'rallies' },
        { id: 'serve_success', kind: 'serve_success', label: 'Successful serves in a row', unit: 'serves' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Active minutes per session', unit: 'min' }
      ],
      3: [
        { id: 'win_rate', kind: 'win_rate', label: 'Match win rate', unit: '%' },
        { id: 'match_wins', kind: 'match_wins', label: 'Match wins this cycle', unit: 'wins' },
        { id: 'ranking_step', kind: 'ranking_step', label: 'Classement points / steps', unit: 'pts' }
      ]
    },
    milestoneOptions: {
      1: [
        { id: 'streak_12w', horizon: 'mid', label: '12-week consistency streak' },
        { id: 'cardio_milestone', horizon: 'mid', label: 'Hit a cardio / HR-zone milestone' },
        { id: 'weekly_habit', horizon: 'long', label: 'Sustain weekly club fitness habit' }
      ],
      2: [
        { id: 'break_rally', horizon: 'mid', label: 'Break a personal rally / exercise record' },
        { id: 'master_serve', horizon: 'mid', label: 'Master a signature serve' },
        { id: 'club_months', horizon: 'long', label: 'Sustain months of regular club play' }
      ],
      3: [
        { id: 'win_champ', horizon: 'long', label: 'Win a championship / league title' },
        { id: 'level_up', horizon: 'mid', label: 'Level up classement category' },
        { id: 'podium', horizon: 'mid', label: 'Podium this season' }
      ]
    },
    relevanceChips: [
      { id: 'confidence', label: 'Build confidence on the table' },
      { id: 'compete', label: 'Compete at a higher level' },
      { id: 'health', label: 'Stay fit and healthy' },
      { id: 'fun', label: 'Keep the joy of playing' },
      { id: 'community', label: 'Belong to the club community' }
    ],
    letStart: 'Access Sports Dashboard'
  },
  fr: {
    // Always vouvoyer (vous / votre / vos). Never tu / toi / ton in player profiling.
    coachName: 'Pingflo',
    typing: 'Pingflo écrit...',
    introTitle: 'SMART Training Tool',
    introText: 'Voici **Pingflo** — l\'hôte de l\'équipe d\'agents SMART de votre club (Pingwi, Pingpro, Pingmynd). Ensemble, construisons votre profil d\'athlète. Pour progresser deux fois plus vite, nous utilisons la méthodologie **SMART** :',
    digipingTag: 'Agent SMART de votre club',
    leisureIntroTitle: 'Challenges & jeux',
    leisureIntroText: 'Voici **Pingflo** — avec **Pingwi** pour les défis ludiques. On repère où vous en êtes et on choisit des **challenges amusants** pour apprendre en jouant.',
    leisureSmartItems: [
      { letter: '🎮', label: "S'amuser", desc: 'Jouer pour le plaisir — sans pression ni jargon.' },
      { letter: '🎯', label: 'Apprendre en jouant', desc: 'De petits défis enseignent les bases sans questionnaire technique.' },
      { letter: '⭐', label: 'Challenges', desc: 'Débloquez des quêtes simples adaptées à votre âge et votre niveau.' }
    ],
    leisureIntroAction: 'Prêt pour votre premier challenge ? Appuyez ci-dessous :',
    leisureStartBtn: 'Lancer le parcours fun',
    leisureAssessmentTitle: 'Votre défi',
    smartItems: [
      { letter: 'S', label: 'Spécifique', desc: 'Nommez les cibles d’entraînement précises qui comptent le plus — un focus ★ principal et des · secondaires optionnels — pour que chaque séance ait un but clair et concret vers votre résultat de saison.' },
      { letter: 'M', label: 'Mesurable', desc: 'Le suivi du progrès des joueurs combine des évaluations subjectives et objectives : auto-évaluations, suivi du coach, et métriques d’exercice issues de l’analyse d’entraînement.' },
      { letter: 'A', label: 'Atteignable', desc: 'Votre coach évalue vos efforts d’entraînement par rapport aux résultats attendus — pour juger si la progression est réaliste et sur la bonne voie.' },
      { letter: 'R', label: 'Pertinent', desc: 'Les objectifs SMART d’entraînement doivent être pertinents — ils doivent compter pour vous et s’aligner sur vos plans plus larges. Le coach les valide et aligne l’entraînement vers les résultats attendus.' },
      { letter: 'T', label: 'Temporel', desc: 'Deux horloges : un sprint de 3 semaines pour les exercices personnalisés sur vos objectifs SMART principaux et secondaires, et une demi-saison pour la présence vers le résultat de saison.' }
    ],
    introAction: 'Alignons votre état d\'esprit ! Appuyez ci-dessous pour commencer :',
    startBtn: 'Commencer le profilage SMART',
    continueBtn: 'Continuer la configuration SMART',
    resumeWelcome: 'Bon retour — reprenons où vous vous étiez arrêté.',
    resumeHint: 'Vous avez une progression enregistrée d\'une session précédente.',
    joinClubBanner: 'Rejoindre {club}',
    editAnswer: 'Modifier',
    editingHint: 'Mettez à jour votre réponse ci-dessous',
    back: 'Retour',
    confirm: 'Confirmer',
    selectOption: 'Sélectionnez une option :',
    enterToContinue: 'Appuyez sur',
    searchCountry: 'Rechercher un pays...',
    searchClub: 'Rechercher un club...',
    enterRank: 'Entrez votre classement/niveau...',
    dobError: 'Veuillez saisir une date de naissance valide (JJ/MM/AAAA)',
    emailError: 'Veuillez saisir une adresse e-mail valide',
    emailExistsHint: 'Cet e-mail est déjà enregistré. Saisissez votre mot de passe pour mettre à jour votre profil et vos parcours SMART.',
    passError: 'Le mot de passe doit comporter au moins 6 caractères',
    passMatchError: 'Les mots de passe ne correspondent pas',
    passWrongError: 'Mot de passe incorrect. Veuillez réessayer.',
    passCreatePlaceholder: 'Créer un mot de passe',
    passConfirmPlaceholder: 'Confirmer le mot de passe',
    passExistingPlaceholder: 'Votre mot de passe',
    saving: 'Inscription et enregistrement du profil...',
    savingUpdate: 'Connexion et mise à jour du profil et des parcours SMART...',
    success: 'Inscription réussie !',
    successUpdate: 'Profil et parcours SMART mis à jour !',
    redirecting: 'Configuration de votre espace sportif personnalisé...',
    emailVerifyHint: 'Nous avons envoyé un lien de confirmation à votre e-mail. Confirmez-le avant de rejoindre une séance — Accueil et Profil sont déjà disponibles.',
    qPurpose: 'Super ! Tout d\'abord, quelle est votre motivation principale dans ce sport ?',
    qExperience: 'Comment décririez-vous votre expérience globale de jeu ?',
    qCompetitionYears: 'Depuis combien d’années jouez-vous en compétition ?',
    qStyle: 'Quel est votre style de jeu préféré à la table ?',
    competitionYearsLt1: 'Moins d’1 an',
    competitionYears1_2: '1–2 ans',
    competitionYears3_5: '3–5 ans',
    competitionYears5_10: '5–10 ans',
    competitionYears10plus: 'Plus de 10 ans',
    qPrimaryFocus: 'Quel est l\'objectif d\'entraînement principal que vous souhaitez cibler en ce moment ?',
    qPrimaryFocusCompetition: 'Selon vous, sur quoi avez-vous besoin de vous entraîner davantage pour vous rapprocher de ces résultats ?',
    qSecondaryFocus: 'Souhaitez-vous ajouter des objectifs secondaires ? Vous pouvez sélectionner jusqu\'à 2 options :',
    qSecondaryFocusCompetition: 'Quels sont les 2 autres aspects de votre jeu où vous vous sentez le moins à l’aise ?',
    qCountry: 'Quel pays représentez-vous en compétition ?',
    qClub: 'Quel club représentez-vous ?',
    qLevel: 'Quel est votre classement officiel FLTT actuel ?',
    qLevelGeneric: 'Quel est votre classement officiel actuel ?',
    qCustomLevel: 'Quel est votre classement officiel actuel dans votre pays ?',
    qGrip: 'Quel type de prise de raquette utilisez-vous ?',
    qHand: 'Quelle est votre main de jeu ?',
    qMetric: 'Quel chiffre allez-vous suivre pour mesurer vos progrès ? Choisissez une métrique et un objectif.',
    qTrainingVolumeCompetition: 'Combien de fois par semaine allez-vous vous entraîner pour atteindre vos objectifs de compétition ? Indiquez séances et heures pour que Pingflo suive vos progrès.',
    qTrainingVolumeRecreational: 'À quelle fréquence jouerez-vous chaque semaine pour rester actif, vous amuser et progresser ? Indiquez séances et heures pour que Pingflo suive vos progrès.',
    qTrainingVolumeHealth: 'Combien de séances par semaine consacrerez-vous à votre santé et votre forme ? Indiquez séances et heures pour que Pingflo suive vos progrès.',
    qTrainingVolume: 'Combien de séances par semaine et combien d’heures par séance allez-vous consacrer à l’entraînement ? Pingflo s’en servira pour suivre vos progrès.',
    sessionsPerWeekLabel: 'Séances par semaine',
    hoursPerWeekLabel: 'Heures par semaine',
    hoursPerSessionLabel: 'Heures par séance',
    sessionsUnit: 'séances/sem',
    hoursUnit: 'h/séance',
    trainingVolumeHealthOk: 'Bravo ! Avec environ {hours} h/semaine (~{minutes} min), vous atteignez ou dépassez la reco OMS adulte de 150 minutes d’activité par semaine. Moins de 18 ans : 60 minutes chaque jour — les séances du club comptent. Pingflo suivra vos progrès — continuez comme ça !',
    trainingVolumeHealthLow: 'Merci ! Vous visez environ {hours} h/semaine (~{minutes} min). Adultes : au moins 150 minutes par semaine. Moins de 18 ans : 60 minutes chaque jour. Les séances du club comptent — envisagez marche, vélo ou séances en plus. Pingflo vous aidera à suivre.',
    qMilestones: 'Quels jalons moyen ou long terme feront avancer votre stratégie ? Jusqu’à 2 — votre coach validera et pourra les redéfinir.',
    qFinalObjective: 'Quel résultat de saison visez-vous ? C’est la destination — ensuite vous choisirez les objectifs SMART ★ principaux et · secondaires qui construisent le chemin.',
    qRelevance: 'Vos objectifs SMART d’entraînement doivent être pertinents — ils doivent compter pour vous et s’aligner sur vos plans plus larges. Votre coach les validera et alignera les efforts d’entraînement pour atteindre les résultats attendus.',
    qRelevanceCompetition: 'Vos objectifs SMART d’entraînement doivent être pertinents — ils doivent compter pour vous et s’aligner sur vos plans plus larges. Votre coach les validera et alignera les efforts d’entraînement pour atteindre les résultats attendus.',
    relevanceContinue: 'Continuer',
    relevanceAckLabel: 'Le coach validera et alignera l’entraînement',
    finalObjectiveHint: 'Choisissez-en un — votre coach pourra affiner la cible ensuite.',
    finalObjectiveKindClassement: 'Monter de classement',
    finalObjectiveKindTeam: 'Gagner/monter division au championnat par équipes',
    finalObjectiveKindTournament: 'Gagner un tournoi',
    finalObjectiveKindOther: 'Autre',
    finalObjectiveOtherPlaceholder: 'Décrivez votre objectif de compétition…',
    qGender: 'Quel est votre genre ?',
    qDob: 'Quelle est votre date de naissance ? (Format JJ/MM/AAAA)',
    qFirstName: 'Quel est votre prénom ? Nous l’utiliserons pour vérifier si vous êtes déjà sur la liste du club.',
    qLastName: 'Quel est votre nom de famille ?',
    qName: 'Quel est votre prénom ? Nous l’utiliserons pour vérifier si vous êtes déjà sur la liste du club.',
    phFirstName: 'Prénom',
    phLastName: 'Nom de famille',
    qEmail: 'Quelle adresse e-mail souhaitez-vous utiliser pour votre compte ?',
    qPassword: 'Enfin, veuillez créer un mot de passe sécurisé (minimum 6 caractères) :',
    qPasswordExisting: 'Cet e-mail existe déjà dans le système. Saisissez votre mot de passe pour mettre à jour votre profil et vos parcours SMART :',
    qCycleLength: 'Les exercices personnalisés sur vos objectifs SMART ★ principaux et · secondaires suivent un sprint de **3 semaines** par défaut (dernière semaine = revue). Le **résultat de saison** (classement, équipe, tournoi) se juge sur une **demi-saison** de présence. Vous pourrez demander à modifier le sprint plus tard **avec l’accord de votre coach**.',
    qCycleLengthLeisure: 'Votre parcours fun suit un cycle de **3 semaines**. Confirmez pour continuer — les challenges viendront plus tard.',
    cycleWeeks3: 'Sprint technique de 3 semaines (par défaut)',
    cycleWeeks4: 'Sprint technique de 4 semaines',
    cycleWeeksHint: '3 semaines par défaut · modifiable plus tard avec accord du coach',
    cycleWeeksConfirm: 'Compris — sprint technique de 3 semaines',
    cycleWeeksLeisureHint: '3 semaines pour ce cycle',
    cycleWeeksLeisureConfirm: 'Compris — cycle de 3 semaines',
    metricTargetPlaceholder: 'Valeur cible…',
    metricCustomLabel: 'Métrique personnalisée',
    milestonesHint: 'Le coach peut affiner les jalons et défis',
    resourceNotePlaceholder: 'Compétences / temps / matériel (optionnel)',
    horizonMid: 'Moyen terme',
    horizonLong: 'Long terme',
    relevanceCustomPlaceholder: 'Ou écrivez pourquoi ça compte…',
    coachPendingCaption: 'Coach',
    coachPendingNote: 'En attente d’accord coach (jalons + pertinence)',
    coachMayRefine: 'Le coach peut affiner',
    achievableSummary: 'Le coach évalue vos efforts d’entraînement par rapport aux résultats attendus',
    coachBanner: 'Continuez quand vous êtes prêt — votre coach finalisera le parcours avec vous.',
    
    assessmentTitle: 'Agent SMART de votre club',
    snapshotEmpty: '—',
    cycleLabel: 'T-court : sprint technique 3 semaines (défaut · modifiable avec le coach) · T-long : demi-saison compétition',
    competitionMindset: 'Excellent ! Puisque vous visez la **Compétition**, votre état d\'esprit doit reposer sur une régularité tactique et une préparation rigoureuse. Pour une approche SMART :\n\n* **Spécifique** : Votre **résultat de saison** (classement, équipe, tournoi) est la destination. Les objectifs SMART ★ primaire et · secondaires (déplacement, coups, …) sont le chemin. Les exercices personnalisés pour ces objectifs viennent après le profilage technique, sur un cycle de 3 semaines.\n* **Mesurable** : Auto-évaluation subjective et métriques d’entraînement objectives sur ces exercices, semaine après semaine.\n* **Atteignable** : Votre coach évalue vos efforts d’entraînement par rapport aux résultats attendus — si la progression est réaliste et sur la bonne voie.\n* **Pertinent** : L’objectif doit compter et s’aligner sur vos plans et objectifs plus larges.\n* **Temporel** : les exercices sur les objectifs ★ et · suivent un sprint d’environ **3 semaines** ; la présence vers le résultat de saison se juge sur une **demi-saison**.',
    recreationalMindset: 'Vous êtes là pour **vous amuser**, **décompresser** et **apprendre quelques bases**. Voici votre plan :\n\n* **Votre défi** — un objectif simple pour les prochaines semaines\n* **Comment vous progressez** — vos séances + une note fun après chaque visite\n* **Quand on fait le point** — dernière semaine du cycle, on choisit le prochain défi',
    qPrimaryFocusLeisure: 'Qu\'est-ce qui vous attire le plus en ce moment ?',
    qFunChallenge: 'Voici votre défi fun — on y va ?',
    funChallengeConfirm: 'C\'est parti !',
    funChallengeGoal: 'Votre objectif',
    funChallengeTrack: 'Comment vous progressez',
    funChallengeMilestone: 'Votre défi',
    funChallengeCycle: 'Votre calendrier',
    healthMindset: 'Vos séances de ping au club comptent pour votre plan santé. Pingflo rappelle la reco OMS, sans en faire un devoir. SMART ici = saison ou phase de 4 mois — bien-être physique, mental et social tissé dans les séances, pas un sprint technique de 3 semaines.',
    metricOptions: {
      1: [
        { id: 'hr_minutes', kind: 'hr_minutes', label: 'Minutes en zone cardio / semaine', unit: 'min' },
        { id: 'streak_weeks', kind: 'streak_weeks', label: 'Série de régularité', unit: 'sem' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Minutes actives par séance', unit: 'min' }
      ],
      2: [
        { id: 'rally_count', kind: 'rally_count', label: 'Échanges consécutifs', unit: 'échanges' },
        { id: 'serve_success', kind: 'serve_success', label: 'Services réussis d’affilée', unit: 'serv.' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Minutes actives par séance', unit: 'min' }
      ],
      3: [
        { id: 'win_rate', kind: 'win_rate', label: 'Taux de victoire', unit: '%' },
        { id: 'match_wins', kind: 'match_wins', label: 'Victoires ce cycle', unit: 'victoires' },
        { id: 'ranking_step', kind: 'ranking_step', label: 'Points / paliers de classement', unit: 'pts' }
      ]
    },
    milestoneOptions: {
      1: [
        { id: 'streak_12w', horizon: 'mid', label: 'Série de régularité sur 12 semaines' },
        { id: 'cardio_milestone', horizon: 'mid', label: 'Atteindre un jalon cardio / FC' },
        { id: 'weekly_habit', horizon: 'long', label: 'Habitude fitness club durable' }
      ],
      2: [
        { id: 'break_rally', horizon: 'mid', label: 'Battre un record d’échange / exercice' },
        { id: 'master_serve', horizon: 'mid', label: 'Maîtriser un service signature' },
        { id: 'club_months', horizon: 'long', label: 'Maintenir plusieurs mois de jeu club' }
      ],
      3: [
        { id: 'win_champ', horizon: 'long', label: 'Gagner un titre / championnat' },
        { id: 'level_up', horizon: 'mid', label: 'Monter de catégorie de classement' },
        { id: 'podium', horizon: 'mid', label: 'Podium cette saison' }
      ]
    },
    relevanceChips: [
      { id: 'confidence', label: 'Gagner en confiance à table' },
      { id: 'compete', label: 'Compétitionner à un niveau supérieur' },
      { id: 'health', label: 'Rester en forme' },
      { id: 'fun', label: 'Garder le plaisir de jouer' },
      { id: 'community', label: 'Faire partie de la communauté du club' }
    ],
    letStart: 'Accéder au Tableau de Bord'
  },
  es: {
    coachName: 'Pingflo',
    typing: 'Pingflo está escribiendo...',
    introTitle: 'SMART Training Tool',
    introText: 'Conoce a **Pingflo** — anfitrión del equipo de agentes SMART de tu club (Pingwi, Pingpro, Pingmynd). Juntos construiremos tu perfil de atleta. Para progresar el doble de rápido, usamos la metodología **SMART**:',
    digipingTag: 'Agent SMART de tu club',
    leisureIntroTitle: 'Challenges y juegos',
    leisureIntroText: 'Conoce a **Pingflo** — con **Pingwi** para retos divertidos. Veremos dónde estás y elegiremos **challenges divertidos** para aprender jugando.',
    leisureSmartItems: [
      { letter: '🎮', label: 'Divertirse', desc: 'Jugar por placer — sin presión ni jerga.' },
      { letter: '🎯', label: 'Aprender jugando', desc: 'Pequeños retos enseñan las bases sin un cuestionario técnico.' },
      { letter: '⭐', label: 'Challenges', desc: 'Desbloquea quests simples adaptadas a tu edad y nivel.' }
    ],
    leisureIntroAction: '¿Listo para tu primer challenge? Pulsa abajo:',
    leisureStartBtn: 'Empezar el camino divertido',
    leisureAssessmentTitle: 'Tu desafío',
    smartItems: [
      { letter: 'S', label: 'Específico', desc: 'Nombra las metas de entrenamiento precisas que más importan — un foco ★ primario y · secundarios opcionales — para que cada sesión tenga un objetivo claro y concreto hacia tu resultado de temporada.' },
      { letter: 'M', label: 'Medible', desc: 'El progreso de los jugadores se sigue con evaluaciones subjetivas y objetivas: autoevaluaciones, seguimiento del coach y métricas de ejercicio del análisis de entrenamiento.' },
      { letter: 'A', label: 'Alcanzable', desc: 'Tu coach evalúa tus esfuerzos de entrenamiento frente a los resultados esperados — para juzgar si el progreso es realista y va por buen camino.' },
      { letter: 'R', label: 'Relevante', desc: 'Los objetivos SMART de entrenamiento deben ser relevantes: deben importarte y encajar con tus planes más amplios. Tu coach los valida y alinea el entrenamiento hacia los resultados esperados.' },
      { letter: 'T', label: 'Temporal', desc: 'Dos relojes: un sprint de 3 semanas para ejercicios personalizados sobre tus objetivos SMART primarios y secundarios, y media temporada de presencia hacia el resultado de temporada.' }
    ],
    introAction: '¡Alineemos tu mentalidad! Pulsa abajo para comenzar:',
    startBtn: 'Comenzar Perfilado SMART',
    continueBtn: 'Continuar configuración SMART',
    resumeWelcome: 'Bienvenido de nuevo — continuamos donde lo dejaste.',
    resumeHint: 'Tienes progreso guardado de una sesión anterior.',
    joinClubBanner: 'Únete a {club}',
    editAnswer: 'Editar',
    editingHint: 'Actualiza tu respuesta abajo',
    back: 'Atrás',
    confirm: 'Confirmar',
    selectOption: 'Selecciona una opción:',
    enterToContinue: 'Pulsa',
    searchCountry: 'Buscar país...',
    searchClub: 'Buscar club...',
    enterRank: 'Introduce tu clasificación/nivel...',
    dobError: 'Introduce una fecha de nacimiento válida (DD/MM/AAAA)',
    emailError: 'Introduce una dirección de correo electrónico válida',
    emailExistsHint: 'Este correo ya está registrado. Introduce tu contraseña para actualizar tu perfil y tus recorridos SMART.',
    passError: 'La contraseña debe tener al menos 6 caracteres',
    passMatchError: 'Las contraseñas no coinciden',
    passWrongError: 'Contraseña incorrecta. Inténtalo de nuevo.',
    passCreatePlaceholder: 'Crear contraseña',
    passConfirmPlaceholder: 'Confirmar contraseña',
    passExistingPlaceholder: 'Tu contraseña',
    saving: 'Registrando y guardando perfil...',
    savingUpdate: 'Iniciando sesión y actualizando tu perfil y recorrido SMART...',
    success: '¡Registro completado!',
    successUpdate: '¡Perfil y recorrido SMART actualizados!',
    redirecting: 'Configurando tu centro deportivo personalizado...',
    emailVerifyHint: 'Te enviamos un enlace de confirmación. Confírmalo antes de unirte a un entrenamiento — Inicio y Perfil ya están disponibles.',
    qPurpose: '¡Excelente! Primero, ¿cuál es tu motivación principal en este deporte?',
    qExperience: '¿Cómo describirías tu nivel de experiencia en general?',
    qCompetitionYears: '¿Cuántos años llevas compitiendo?',
    qStyle: '¿Cuál es tu estilo de juego preferido en la mesa?',
    competitionYearsLt1: 'Menos de 1 año',
    competitionYears1_2: '1–2 años',
    competitionYears3_5: '3–5 años',
    competitionYears5_10: '5–10 años',
    competitionYears10plus: 'Más de 10 años',
    qPrimaryFocus: '¿Cuál es el enfoque de entrenamiento principal que quieres priorizar ahora mismo?',
    qPrimaryFocusCompetition: '¿En qué crees que necesitas entrenar más para acercarte a esos resultados?',
    qSecondaryFocus: '¿Hay enfoques secundarios que quieras añadir? Puedes elegir hasta 2 opciones:',
    qSecondaryFocusCompetition: '¿Cuáles son los 2 otros aspectos de tu juego en los que te sientes menos seguro?',
    qCountry: '¿A qué país representas en competición?',
    qClub: '¿A qué club representas?',
    qLevel: '¿Cuál es tu clasificación oficial FLTT actual?',
    qLevelGeneric: '¿Cuál es tu clasificación oficial actual?',
    qCustomLevel: '¿Cuál es tu clasificación oficial en tu país?',
    qGrip: '¿Qué tipo de agarre de raqueta utilizas?',
    qHand: '¿Cuál es tu mano de juego?',
    qMetric: '¿Qué número vas a seguir para medir el progreso? Elige una métrica y un objetivo.',
    qTrainingVolumeCompetition: '¿Cuántas veces por semana entrenarás para alcanzar tus objetivos de competición? Indica sesiones y horas para que Pingflo siga tu progreso.',
    qTrainingVolumeRecreational: '¿Con qué frecuencia jugarás cada semana para mantenerte activo, divertirte y mejorar? Indica sesiones y horas para que Pingflo siga tu progreso.',
    qTrainingVolumeHealth: '¿Cuántas sesiones por semana dedicarás a tu salud y forma física? Indica sesiones y horas para que Pingflo siga tu progreso.',
    qTrainingVolume: '¿Cuántas sesiones por semana y cuántas horas por sesión dedicarás al entrenamiento? Pingflo lo usará para seguir tu progreso.',
    sessionsPerWeekLabel: 'Sesiones por semana',
    hoursPerWeekLabel: 'Horas por semana',
    hoursPerSessionLabel: 'Horas por sesión',
    sessionsUnit: 'sesiones/sem',
    hoursUnit: 'h/sesión',
    trainingVolumeHealthOk: '¡Bien hecho! Con unas {hours} h/semana (~{minutes} min) alcanzas o superas la pauta OMS de adultos de 150 minutos de actividad por semana. Menores de 18: 60 minutos cada día — las sesiones del club cuentan. Pingflo seguirá tu progreso — ¡sigue así!',
    trainingVolumeHealthLow: 'Entendido: unas {hours} h/semana (~{minutes} min). Adultos: al menos 150 minutos por semana. Menores de 18: 60 minutos cada día. Las sesiones del club cuentan — valora caminar, bici o más sesiones. Pingflo te ayudará a seguirlo.',
    qMilestones: '¿Qué hitos a medio o largo plazo impulsan tu estrategia? Hasta 2 — tu coach los validará y podrá redefinirlos.',
    qFinalObjective: '¿Qué resultado de temporada persigues? Es el destino — después elegirás los objetivos SMART ★ principales y · secundarios que construyen el camino.',
    qRelevance: 'Tus objetivos SMART de entrenamiento deben ser relevantes: deben importarte y encajar con tus planes más amplios. Tu coach los validará y alineará el entrenamiento para alcanzar los resultados esperados.',
    qRelevanceCompetition: 'Tus objetivos SMART de entrenamiento deben ser relevantes: deben importarte y encajar con tus planes más amplios. Tu coach los validará y alineará el entrenamiento para alcanzar los resultados esperados.',
    relevanceContinue: 'Continuar',
    relevanceAckLabel: 'El coach validará y alineará el entrenamiento',
    finalObjectiveHint: 'Elige uno — tu coach podrá afinar la meta después.',
    finalObjectiveKindClassement: 'Subir de clasificación',
    finalObjectiveKindTeam: 'Ganar / subir de división en el campeonato por equipos',
    finalObjectiveKindTournament: 'Ganar un torneo',
    finalObjectiveKindOther: 'Otro',
    finalObjectiveOtherPlaceholder: 'Describe tu objetivo de competición…',
    qGender: '¿Cuál es tu género?',
    qDob: '¿Cuál es tu fecha de nacimiento? (Usa el formato DD/MM/AAAA)',
    qFirstName: '¿Cuál es tu nombre? Lo usamos para comprobar si ya estás en la lista del club.',
    qLastName: '¿Cuál es tu apellido?',
    qName: '¿Cuál es tu nombre? Lo usamos para comprobar si ya estás en la lista del club.',
    phFirstName: 'Nombre',
    phLastName: 'Apellido',
    qEmail: '¿Qué correo electrónico te gustaría usar para tu cuenta?',
    qPassword: 'Por último, crea una contraseña segura (mínimo 6 caracteres):',
    qPasswordExisting: 'Este correo ya existe en el sistema. Introduce tu contraseña para actualizar tu perfil y tu recorrido SMART:',
    qCycleLength: 'Los ejercicios personalizados sobre tus objetivos SMART ★ principales y · secundarios siguen un sprint de **3 semanas** por defecto (última semana = revisión). El **resultado de temporada** (clasificación, equipo, torneo) se juzga en **media temporada** de presencia. Podrás pedir cambiar el sprint más adelante **con el acuerdo de tu coach**.',
    qCycleLengthLeisure: 'Tu camino divertido sigue un ciclo de **3 semanas**. Confirma para continuar — los challenges vendrán más adelante.',
    cycleWeeks3: 'Sprint técnico de 3 semanas (por defecto)',
    cycleWeeks4: 'Sprint técnico de 4 semanas',
    cycleWeeksHint: '3 semanas por defecto · cambio posterior con acuerdo del coach',
    cycleWeeksConfirm: 'Entendido — sprint técnico de 3 semanas',
    cycleWeeksLeisureHint: '3 semanas para este ciclo',
    cycleWeeksLeisureConfirm: 'Entendido — ciclo de 3 semanas',
    metricTargetPlaceholder: 'Número objetivo…',
    metricCustomLabel: 'Métrica personalizada',
    milestonesHint: 'El coach puede afinar hitos y retos',
    resourceNotePlaceholder: 'Habilidades / tiempo / material (opcional)',
    horizonMid: 'Medio plazo',
    horizonLong: 'Largo plazo',
    relevanceCustomPlaceholder: 'O escribe por qué importa…',
    coachPendingCaption: 'Coach',
    coachPendingNote: 'Pendiente de acuerdo del coach (hitos + relevancia)',
    coachMayRefine: 'El coach puede afinar',
    achievableSummary: 'El coach evalúa tus esfuerzos de entrenamiento frente a los resultados esperados',
    coachBanner: 'Continúa cuando estés listo — tu coach cerrará el recorrido contigo.',
    
    assessmentTitle: 'Agent SMART de tu club',
    snapshotEmpty: '—',
    cycleLabel: 'T-corto: sprint técnico 3 semanas · T-largo: media temporada para competición',
    competitionMindset: '¡Estupendo! Al enfocarte en la **Competición**, tu mentalidad debe basarse en una alta regularidad táctica y preparación rigurosa. Tu enfoque SMART:\n\n* **Específico**: Tu **resultado de temporada** (clasificación, equipo, torneo) es el destino. Los objetivos SMART ★ primario y · secundarios (desplazamiento, golpes, …) son el camino. Los ejercicios personalizados para esos objetivos llegan tras el perfil técnico, en un ciclo de 3 semanas.\n* **Medible**: Autoeval subjetiva y métricas de entrenamiento objetivas sobre esos ejercicios, semana a semana.\n* **Alcanzable**: Tu coach evalúa tus esfuerzos de entrenamiento frente a los resultados esperados — si el progreso es realista y va por buen camino.\n* **Relevante**: El objetivo debe importar y encajar con tus planes y objetivos más amplios.\n* **Temporal**: los ejercicios sobre objetivos ★ y · van en un sprint de unas **3 semanas**; la presencia hacia el resultado de temporada se juzga en **media temporada**.',
    recreationalMindset: 'Estás aquí para **divertirte**, **desconectar** y **aprender unas bases**. Pingflo te propondrá **challenges y juegos** alrededor de habilidades de principiante:\n\n* **Tu desafío** — una quest simple que puedes disfrutar en el club\n* **Cómo lo sigues** — sesiones y una nota fun tras cada visita\n* **Qué sigue** — desbloquear el siguiente challenge cuando estés listo',
    qPrimaryFocusLeisure: '¿Qué te apetece más ahora mismo?',
    qFunChallenge: 'Aquí está tu challenge divertido — ¿listo?',
    funChallengeConfirm: '¡Vamos!',
    funChallengeGoal: 'Tu objetivo',
    funChallengeTrack: 'Cómo mides el progreso',
    funChallengeMilestone: 'Tu desafío',
    funChallengeCycle: 'Tu calendario',
    healthMindset: 'Tus sesiones de ping en el club cuentan para tu plan de salud. Pingflo recuerda la guía OMS sin convertirla en deberes. SMART aquí es temporada o fase de 4 meses — bienestar físico, mental y social en las sesiones, no un sprint técnico de 3 semanas.',
    metricOptions: {
      1: [
        { id: 'hr_minutes', kind: 'hr_minutes', label: 'Minutos en zona cardíaca / semana', unit: 'min' },
        { id: 'streak_weeks', kind: 'streak_weeks', label: 'Racha de constancia', unit: 'sem' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Minutos activos por sesión', unit: 'min' }
      ],
      2: [
        { id: 'rally_count', kind: 'rally_count', label: 'Peloteos consecutivos', unit: 'peloteos' },
        { id: 'serve_success', kind: 'serve_success', label: 'Saques exitosos seguidos', unit: 'saques' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Minutos activos por sesión', unit: 'min' }
      ],
      3: [
        { id: 'win_rate', kind: 'win_rate', label: 'Porcentaje de victorias', unit: '%' },
        { id: 'match_wins', kind: 'match_wins', label: 'Victorias este ciclo', unit: 'victorias' },
        { id: 'ranking_step', kind: 'ranking_step', label: 'Puntos / peldaños de ranking', unit: 'pts' }
      ]
    },
    milestoneOptions: {
      1: [
        { id: 'streak_12w', horizon: 'mid', label: 'Racha de constancia de 12 semanas' },
        { id: 'cardio_milestone', horizon: 'mid', label: 'Alcanzar un hito cardio / FC' },
        { id: 'weekly_habit', horizon: 'long', label: 'Hábito fitness de club sostenible' }
      ],
      2: [
        { id: 'break_rally', horizon: 'mid', label: 'Batir un récord de peloteo / ejercicio' },
        { id: 'master_serve', horizon: 'mid', label: 'Dominar un saque signature' },
        { id: 'club_months', horizon: 'long', label: 'Mantener meses de juego regular en club' }
      ],
      3: [
        { id: 'win_champ', horizon: 'long', label: 'Ganar un campeonato / liga' },
        { id: 'level_up', horizon: 'mid', label: 'Subir de categoría de ranking' },
        { id: 'podium', horizon: 'mid', label: 'Podio esta temporada' }
      ]
    },
    relevanceChips: [
      { id: 'confidence', label: 'Ganar confianza en la mesa' },
      { id: 'compete', label: 'Competir a un nivel superior' },
      { id: 'health', label: 'Mantenerse en forma' },
      { id: 'fun', label: 'Conservar la alegría de jugar' },
      { id: 'community', label: 'Pertenecer a la comunidad del club' }
    ],
    letStart: 'Entrar al Panel de Control'
  },
  de: {
    coachName: 'Pingflo',
    typing: 'Pingflo schreibt...',
    introTitle: 'SMART Training Tool',
    introText: 'Das ist **Pingflo** — Gastgeber des SMART-Agententeams Ihres Clubs (Pingwi, Pingpro, Pingmynd). Gemeinsam bauen wir dein Athletenprofil. Damit du doppelt so schnell Fortschritte machst, nutzen wir die **SMART**-Methode:',
    digipingTag: 'SMART-Agent deines Clubs',
    leisureIntroTitle: 'Challenges & Spiele',
    leisureIntroText: 'Das ist **Pingflo** — mit **Pingwi** für spielerische Challenges. Wir schauen, wo du stehst, und wählen **lustige Challenges**, damit Lernen wie ein Spiel wirkt.',
    leisureSmartItems: [
      { letter: '🎮', label: 'Spaß haben', desc: 'Spielen aus Freude — ohne Druck, ohne Jargon.' },
      { letter: '🎯', label: 'Spielend lernen', desc: 'Kleine Challenges vermitteln die Basics ohne schweren Fragebogen.' },
      { letter: '⭐', label: 'Challenges', desc: 'Schalte einfache Quests frei, passend zu Alter und Niveau.' }
    ],
    leisureIntroAction: 'Bereit für deine erste Challenge? Tippe unten:',
    leisureStartBtn: 'Fun-Pfad starten',
    leisureAssessmentTitle: 'Deine Challenge',
    smartItems: [
      { letter: 'S', label: 'Spezifisch', desc: 'Benenne die präzisen Trainingsziele, die am meisten zählen — einen ★-Hauptfokus und optionale ·-Nebenziele —, damit jede Einheit ein klares, konkretes Ziel auf dem Weg zu deinem Saisonergebnis hat.' },
      { letter: 'M', label: 'Messbar', desc: 'Der Spielerfortschritt wird durch subjektive und objektive Bewertungen erfasst: Selbsteinschätzungen, Coach-Begleitung und Übungsmetriken aus der Trainingsanalyse.' },
      { letter: 'A', label: 'Erreichbar', desc: 'Dein Coach bewertet deine Trainingsanstrengungen im Vergleich zu den erwarteten Ergebnissen — ob der Fortschritt realistisch und auf Kurs ist.' },
      { letter: 'R', label: 'Relevant', desc: 'SMART-Trainingsziele müssen relevant sein — sie müssen für dich zählen und zu deinen weiteren Plänen passen. Dein Coach validiert sie und richtet das Training auf die erwarteten Ergebnisse aus.' },
      { letter: 'T', label: 'Terminiert', desc: 'Zwei Uhren: ein 3-Wochen-Sprint für personalisierte Übungen auf deinen primären und sekundären SMART-Zielen, und eine Halbsaison für die Anwesenheit Richtung Saisonergebnis.' }
    ],
    introAction: 'Lass uns dein Mindset ausrichten! Drücke unten, um zu starten:',
    startBtn: 'SMART Profiling starten',
    continueBtn: 'SMART-Einrichtung fortsetzen',
    resumeWelcome: 'Willkommen zurück — wir machen da weiter, wo du aufgehört hast.',
    resumeHint: 'Du hast gespeicherten Fortschritt aus einer früheren Sitzung.',
    joinClubBanner: 'Tritt {club} bei',
    editAnswer: 'Bearbeiten',
    editingHint: 'Aktualisiere deine Antwort unten',
    back: 'Zurück',
    confirm: 'Bestätigen',
    selectOption: 'Wähle eine Option:',
    enterToContinue: 'Drücke',
    searchCountry: 'Land suchen...',
    searchClub: 'Verein suchen...',
    enterRank: 'Gib deine Spielstärke/Klassierung ein...',
    dobError: 'Bitte gib ein gültiges Geburtsdatum ein (TT.MM.JJJJ)',
    emailError: 'Bitte gib eine gültige E-Mail-Adresse ein',
    emailExistsHint: 'Diese E-Mail ist bereits registriert. Gib dein Passwort ein, um Profil und SMART-Pfad zu aktualisieren.',
    passError: 'Das Passwort muss mindestens 6 Zeichen lang sein',
    passMatchError: 'Die Passwörter stimmen nicht überein',
    passWrongError: 'Falsches Passwort. Bitte erneut versuchen.',
    passCreatePlaceholder: 'Passwort erstellen',
    passConfirmPlaceholder: 'Passwort bestätigen',
    passExistingPlaceholder: 'Dein Passwort',
    saving: 'Registrierung & Profil wird gespeichert...',
    savingUpdate: 'Anmeldung und Aktualisierung von Profil & SMART-Pfad...',
    success: 'Registrierung erfolgreich!',
    successUpdate: 'Profil und SMART-Pfad aktualisiert!',
    redirecting: 'Dein personalisierter Sportbereich wird eingerichtet...',
    emailVerifyHint: 'Wir haben einen Bestätigungslink an deine E-Mail gesendet. Bestätige ihn, bevor du an einer Trainingseinheit teilnimmst — Startseite und Profil sind schon verfügbar.',
    qPurpose: 'Hervorragend! Was ist deine primäre Motivation in diesem Sport?',
    qExperience: 'Wie würdest du deine allgemeine Spielerfahrung beschreiben?',
    qCompetitionYears: 'Seit wie vielen Jahren spielst du im Wettkampf?',
    qStyle: 'Welchen Spielstil bevorzugst du an der Platte?',
    competitionYearsLt1: 'Weniger als 1 Jahr',
    competitionYears1_2: '1–2 Jahre',
    competitionYears3_5: '3–5 Jahre',
    competitionYears5_10: '5–10 Jahre',
    competitionYears10plus: 'Mehr als 10 Jahre',
    qPrimaryFocus: 'Welchen Trainingsschwerpunkt möchtest du im Moment priorisieren?',
    qPrimaryFocusCompetition: 'Woran denkst du, musst du mehr trainieren, um näher an diese Ergebnisse zu kommen?',
    qSecondaryFocus: 'Möchtest du sekundäre Schwerpunkte hinzufügen? Wähle bis zu 2 Optionen:',
    qSecondaryFocusCompetition: 'Bei welchen 2 anderen Aspekten deines Spiels fühlst du dich am wenigsten sicher?',
    qCountry: 'Welches Land vertrittst du im Wettkampf?',
    qClub: 'Welchen Verein vertrittst du?',
    qLevel: 'Wie lautet deine aktuelle offizielle FLTT-Klassierung?',
    qLevelGeneric: 'Wie lautet deine aktuelle offizielle Klassierung?',
    qCustomLevel: 'Wie lautet deine aktuelle offizielle Klassierung in deinem Land?',
    qGrip: 'Welche Schlägerhaltung (Griffart) nutzt du?',
    qHand: 'Was ist deine Spielhand?',
    qMetric: 'Welche Zahl verfolgst du, um Fortschritt zu messen? Wähle eine Metrik und ein Ziel.',
    qTrainingVolumeCompetition: 'Wie oft pro Woche wirst du trainieren, um deine Wettkampfziele zu erreichen? Gib Einheiten und Stunden an, damit Pingflo deinen Fortschritt verfolgen kann.',
    qTrainingVolumeRecreational: 'Wie oft wirst du pro Woche spielen, um aktiv zu bleiben, Spaß zu haben und dich zu verbessern? Gib Einheiten und Stunden an, damit Pingflo deinen Fortschritt verfolgen kann.',
    qTrainingVolumeHealth: 'Wie viele Einheiten pro Woche widmest du Gesundheit und Fitness? Gib Einheiten und Stunden an, damit Pingflo deinen Fortschritt verfolgen kann.',
    qTrainingVolume: 'Wie viele Einheiten pro Woche und wie viele Stunden pro Einheit wirst du trainieren? Pingflo nutzt das für deinen Fortschritt.',
    sessionsPerWeekLabel: 'Einheiten pro Woche',
    hoursPerWeekLabel: 'Stunden pro Woche',
    hoursPerSessionLabel: 'Stunden pro Einheit',
    sessionsUnit: 'Einheiten/Wo',
    hoursUnit: 'h/Einheit',
    trainingVolumeHealthOk: 'Super! Mit etwa {hours} h/Woche (~{minutes} Min) erreichst oder übertriffst du die WHO-Empfehlung für Erwachsene von 150 Minuten Bewegung pro Woche. Unter 18: 60 Minuten täglich — Vereinseinheiten zählen mit. Pingflo verfolgt deinen Fortschritt — weiter so!',
    trainingVolumeHealthLow: 'Alles klar — etwa {hours} h/Woche (~{minutes} Min). Erwachsene: mindestens 150 Minuten pro Woche. Unter 18: 60 Minuten täglich. Vereinseinheiten zählen mit — überlege Gehen, Radfahren oder extra Einheiten. Pingflo hilft dir dabei.',
    qMilestones: 'Welche mittel- oder langfristigen Meilensteine treiben deine Strategie? Bis zu 2 — dein Coach validiert und kann sie neu ausrichten.',
    qFinalObjective: 'Welches Saisonergebnis strebst du an? Das ist das Ziel — als Nächstes wählst du SMART-★-Haupt- und ·-Nebenziele, die den Weg bauen.',
    qRelevance: 'Deine SMART-Trainingsziele sollten relevant sein — sie müssen für dich zählen und zu deinen weiteren Plänen passen. Dein Coach validiert sie und richtet das Training so aus, dass du die erwarteten Ergebnisse erreichst.',
    qRelevanceCompetition: 'Deine SMART-Trainingsziele sollten relevant sein — sie müssen für dich zählen und zu deinen weiteren Plänen passen. Dein Coach validiert sie und richtet das Training so aus, dass du die erwarteten Ergebnisse erreichst.',
    relevanceContinue: 'Weiter',
    relevanceAckLabel: 'Coach validiert und richtet das Training aus',
    finalObjectiveHint: 'Wähle eines — dein Coach kann das Ziel später schärfen.',
    finalObjectiveKindClassement: 'Im Ranking aufsteigen',
    finalObjectiveKindTeam: 'Im Mannschaftswettbewerb gewinnen / aufsteigen',
    finalObjectiveKindTournament: 'Ein Turnier gewinnen',
    finalObjectiveKindOther: 'Sonstiges',
    finalObjectiveOtherPlaceholder: 'Beschreibe dein Wettkampfziel…',
    qGender: 'Was ist dein Geschlecht?',
    qDob: 'Wie lautet dein Geburtsdatum? (Bitte im Format TT.MM.JJJJ eingeben)',
    qFirstName: 'Wie lautet dein Vorname? Damit prüfen wir, ob du schon auf der Vereinsliste stehst.',
    qLastName: 'Wie lautet dein Nachname?',
    qName: 'Wie lautet dein Vorname? Damit prüfen wir, ob du schon auf der Vereinsliste stehst.',
    phFirstName: 'Vorname',
    phLastName: 'Nachname',
    qEmail: 'Welche E-Mail-Adresse möchtest du für dein Konto nutzen?',
    qPassword: 'Erstelle bitte ein sicheres Passwort (mindestens 6 Zeichen):',
    qPasswordExisting: 'Diese E-Mail ist bereits im System. Gib dein Passwort ein, um Profil und SMART-Pfad zu aktualisieren:',
    qCycleLength: 'Personalisierte Übungen auf deinen SMART-★-Haupt- und ·-Nebenzielen laufen standardmäßig in einem **3-Wochen**-Sprint (letzte Woche = Review). Das **Saisonergebnis** (Klassierung, Team, Turnier) wird über eine **Halbsaison** Anwesenheit beurteilt. Später kannst du eine Änderung **mit Zustimmung deines Coaches** anfragen.',
    qCycleLengthLeisure: 'Dein Fun-Pfad läuft in einem **3-Wochen**-Zyklus. Bestätige zum Weitergehen — Challenges kommen später.',
    cycleWeeks3: '3-Wochen-Technik-Sprint (Standard)',
    cycleWeeks4: '4-Wochen-Technik-Sprint',
    cycleWeeksHint: 'Standard 3 Wochen · später änderbar mit Coach-Zustimmung',
    cycleWeeksConfirm: 'Verstanden — 3-Wochen-Technik-Sprint',
    cycleWeeksLeisureHint: '3 Wochen für diesen Zyklus',
    cycleWeeksLeisureConfirm: 'Verstanden — 3-Wochen-Zyklus',
    metricTargetPlaceholder: 'Zielwert…',
    metricCustomLabel: 'Eigene Metrik',
    milestonesHint: 'Coach kann Meilensteine und Challenges schärfen',
    resourceNotePlaceholder: 'Skills / Zeit / Material (optional)',
    horizonMid: 'Mittelfristig',
    horizonLong: 'Langfristig',
    relevanceCustomPlaceholder: 'Oder schreibe, warum es zählt…',
    coachPendingCaption: 'Coach',
    coachPendingNote: 'Warte auf Coach-Abstimmung (Meilensteine + Relevanz)',
    coachMayRefine: 'Coach kann schärfen',
    achievableSummary: 'Der Coach bewertet deine Trainingsanstrengungen im Vergleich zu den erwarteten Ergebnissen',
    coachBanner: 'Weiter, wenn du bereit bist — dein Coach schließt den Weg mit dir ab.',
    
    assessmentTitle: 'SMART-Agent deines Clubs',
    snapshotEmpty: '—',
    cycleLabel: 'T-kurz: 3-Wochen-Technik-Sprint · T-lang: Halbsaison für Wettkampf',
    competitionMindset: 'Großartig! Da dein Fokus auf **Wettkampf** liegt, basiert dein Mindset auf hoher taktischer Konstanz und professioneller Vorbereitung. Dein SMART-Ansatz:\n\n* **Spezifisch**: Dein **Saisonergebnis** (Klassierung, Team, Turnier) ist das Ziel. SMART-★-Haupt- und ·-Nebenziele (Beinarbeit, Schläge, …) sind der Weg. Personalisierte Übungen für diese Ziele folgen nach dem technischen Profiling, in einem 3-Wochen-Zyklus.\n* **Messbar**: Subjektive Autoeval plus objektive Trainingsmetriken auf diesen Übungen, Woche für Woche.\n* **Erreichbar**: Dein Coach bewertet deine Trainingsanstrengungen im Vergleich zu den erwarteten Ergebnissen — ob der Fortschritt realistisch und auf Kurs ist.\n* **Relevant**: Das Ziel muss zählen und zu deinen weiteren Plänen und Zielen passen.\n* **Terminiert**: Übungen auf ★- und ·-Zielen laufen in einem **~3-Wochen**-Sprint; die Anwesenheit Richtung Saisonergebnis wird über eine **Halbsaison** beurteilt.',
    recreationalMindset: 'Du bist hier, um **Spaß zu haben**, **abzuschalten** und **ein paar Basics zu lernen**. Pingflo füttert dich mit **Challenges & Spielen** rund um Anfänger-Skills:\n\n* **Deine Challenge** — eine einfache Quest, die du im Club genießen kannst\n* **So trackst du** — Einheiten und eine Fun-Note nach jedem Besuch\n* **Was als Nächstes** — die nächste Challenge freischalten, wenn du bereit bist',
    qPrimaryFocusLeisure: 'Was klingt gerade am meisten nach Spaß?',
    qFunChallenge: 'Hier ist deine Fun-Challenge — bereit?',
    funChallengeConfirm: 'Los geht’s!',
    funChallengeGoal: 'Dein Ziel',
    funChallengeTrack: 'So misst du Fortschritt',
    funChallengeMilestone: 'Deine Challenge',
    funChallengeCycle: 'Dein Kalender',
    healthMindset: 'Deine Vereinseinheiten zählen zu deinem Gesundheitsplan. Pingflo erinnert an die WHO-Empfehlung, ohne Hausaufgaben daraus zu machen. SMART hier = Saison oder 4-Monats-Phase — körperliches, mentales und soziales Wohlbefinden in den Einheiten, kein 3-Wochen-Technik-Sprint.',
    metricOptions: {
      1: [
        { id: 'hr_minutes', kind: 'hr_minutes', label: 'HF-Zonen-Minuten / Woche', unit: 'min' },
        { id: 'streak_weeks', kind: 'streak_weeks', label: 'Konstanz-Serie', unit: 'Wo' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Aktive Minuten pro Einheit', unit: 'min' }
      ],
      2: [
        { id: 'rally_count', kind: 'rally_count', label: 'Ballwechsel in Folge', unit: 'Wechsel' },
        { id: 'serve_success', kind: 'serve_success', label: 'Erfolgreiche Aufschläge in Folge', unit: 'Aufschläge' },
        { id: 'active_minutes', kind: 'active_minutes', label: 'Aktive Minuten pro Einheit', unit: 'min' }
      ],
      3: [
        { id: 'win_rate', kind: 'win_rate', label: 'Siegquote', unit: '%' },
        { id: 'match_wins', kind: 'match_wins', label: 'Siege in diesem Zyklus', unit: 'Siege' },
        { id: 'ranking_step', kind: 'ranking_step', label: 'Klassierungspunkte / Stufen', unit: 'Pkt' }
      ]
    },
    milestoneOptions: {
      1: [
        { id: 'streak_12w', horizon: 'mid', label: '12-Wochen-Konstanz-Serie' },
        { id: 'cardio_milestone', horizon: 'mid', label: 'Cardio- / HF-Meilenstein erreichen' },
        { id: 'weekly_habit', horizon: 'long', label: 'Nachhaltige Club-Fitness-Gewohnheit' }
      ],
      2: [
        { id: 'break_rally', horizon: 'mid', label: 'Persönlichen Ballwechsel-/Übungsrekord brechen' },
        { id: 'master_serve', horizon: 'mid', label: 'Signature-Aufschlag meistern' },
        { id: 'club_months', horizon: 'long', label: 'Monate regelmäßiges Clubspiel halten' }
      ],
      3: [
        { id: 'win_champ', horizon: 'long', label: 'Meisterschaft / Ligatitel gewinnen' },
        { id: 'level_up', horizon: 'mid', label: 'Klassierungskategorie hochziehen' },
        { id: 'podium', horizon: 'mid', label: 'Podium in dieser Saison' }
      ]
    },
    relevanceChips: [
      { id: 'confidence', label: 'Mehr Selbstvertrauen an der Platte' },
      { id: 'compete', label: 'Auf höherem Niveau konkurrieren' },
      { id: 'health', label: 'Fit und gesund bleiben' },
      { id: 'fun', label: 'Die Freude am Spiel behalten' },
      { id: 'community', label: 'Zur Club-Community gehören' }
    ],
    letStart: 'Zum Dashboard wechseln'
  }
};

/** Dutch / Portuguese: English base + leisure path + Pingflo tag localized. */
TRANSLATIONS.nl = {
  ...TRANSLATIONS.en,
  digipingTag: 'SMART-Agent van je club',
  leisureIntroTitle: 'Challenges & spelletjes',
  leisureIntroText: 'Maak kennis met **Pingflo** — je speelse sidekick. We kijken waar je staat en kiezen **leuke challenges** zodat leren aanvoelt als een spel.',
  leisureSmartItems: [
    { letter: '🎮', label: 'Plezier hebben', desc: 'Spelen voor de lol — zonder druk, zonder jargon.' },
    { letter: '🎯', label: 'Leren door te spelen', desc: 'Kleine challenges leren de basis zonder zware vragenlijst.' },
    { letter: '⭐', label: 'Challenges', desc: 'Ontgrendel eenvoudige quests en drills op jouw leeftijd en niveau.' }
  ],
  leisureIntroAction: 'Klaar voor je eerste challenge? Tik hieronder:',
  leisureStartBtn: 'Start het fun-pad',
  leisureAssessmentTitle: 'Jouw challenge',
  recreationalMindset:
    'Je bent hier om te **genieten**, te **ontspannen** en **een paar basics** te leren. Pingflo voedt je met **challenges & spelletjes** rond beginner-skills:\n\n* **Jouw challenge** — een simpele quest die je in de club kunt doen\n* **Zo volg je het** — sessies en een fun-score na elk bezoek\n* **Wat volgt** — de volgende challenge ontgrendelen wanneer je klaar bent',
  qPrimaryFocusLeisure: 'Wat klinkt nu het leukst?',
  qFunChallenge: 'Hier is je fun-challenge — klaar?',
  funChallengeConfirm: 'Laten we gaan!',
  funChallengeGoal: 'Jouw doel',
  funChallengeTrack: 'Zo meet je voortgang',
  funChallengeMilestone: 'Jouw challenge',
  funChallengeCycle: 'Jouw kalender',
  introText:
    'Maak kennis met **Pingflo** — gastheer van het SMART-agententeam van je club (Pingwi, Pingpro, Pingmynd). Samen bouwen we je atletenprofiel. Om twee keer zo snel te groeien gebruiken we de **SMART**-methodologie:',
};

TRANSLATIONS.pt = {
  ...TRANSLATIONS.en,
  digipingTag: 'Agente SMART do teu clube',
  leisureIntroTitle: 'Challenges e jogos',
  leisureIntroText: 'Conhece o **Pingflo** — o teu companheiro lúdico. Vamos ver onde estás e escolher **challenges divertidos** para aprender a brincar.',
  leisureSmartItems: [
    { letter: '🎮', label: 'Divertir-se', desc: 'Jogar por prazer — sem pressão nem jargão.' },
    { letter: '🎯', label: 'Aprender a jogar', desc: 'Pequenos desafios ensinam as bases sem questionário técnico.' },
    { letter: '⭐', label: 'Challenges', desc: 'Desbloqueia quests simples adaptadas à tua idade e nível.' }
  ],
  leisureIntroAction: 'Pronto para o teu primeiro challenge? Carrega abaixo:',
  leisureStartBtn: 'Começar o percurso divertido',
  leisureAssessmentTitle: 'O teu desafio',
  recreationalMindset:
    'Estás aqui para te **divertires**, **descontraires** e **aprenderes umas bases**. O Pingflo propõe-te **challenges e jogos** em torno de competências de principiante:\n\n* **O teu desafio** — uma quest simples para desfrutares no clube\n* **Como acompanhas** — sessões e uma nota fun após cada visita\n* **O que se segue** — desbloquear o próximo challenge quando estiveres pronto',
  qPrimaryFocusLeisure: 'O que te apetece mais agora?',
  qFunChallenge: 'Aqui está o teu challenge divertido — pronto?',
  funChallengeConfirm: 'Vamos!',
  funChallengeGoal: 'O teu objetivo',
  funChallengeTrack: 'Como meds o progresso',
  funChallengeMilestone: 'O teu desafio',
  funChallengeCycle: 'O teu calendário',
  introText:
    'Conhece o **Pingflo** — anfitrião da equipa de agentes SMART do teu clube (Pingwi, Pingpro, Pingmynd). Juntos construímos o teu perfil de atleta. Para progredir duas vezes mais depressa, usamos a metodologia **SMART**:',
};

const HEALTH_WIZARD_COPY = {
  en: {},
  fr: {
    healthSmartItems: [
      { letter: 'S', label: 'Spécifique', desc: 'Faire grandir le bien-être physique, mental et/ou social par le ping au club.' },
      { letter: 'M', label: 'Mesurable', desc: "Minutes OMS pour votre âge plus les séances club que vous pouvez tenir. Le ping club compte pour le total." },
      { letter: 'A', label: 'Atteignable', desc: "Vous aurez des jalons d'entraînement qui soutiennent vos objectifs physiques et mentaux." },
      { letter: 'R', label: 'Pertinent', desc: 'Votre coach veillera à ce que le plan soit pertinent pour vos objectifs.' },
      { letter: 'T', label: 'Temporel', desc: 'Une saison ou une phase de 4 mois, puis bilan avec le club.' }
    ],
    qTrainingVolumeHealth: "Combien de séances club par semaine pouvez-vous tenir ? Le ping compte pour le total OMS — ce n'est pas un quota ping seul.",
    qHealthIntro: "La santé dans un club, c'est le bien-être physique, mental et social. Le tennis de table est un cadre naturel Sports Club for Health — on le tisse dans les séances, pas à côté.",
    qHealthDimensions: 'Quelles dimensions doivent grandir pour vous ? Sélectionnez tout ce qui s’applique.',
    qHealthWhoCheck: "Voici la reco OMS pour votre âge. Les séances club comptent. Où en êtes-vous aujourd'hui ?",
    qHealthDelivery: 'Votre coach veillera à ce que le plan soit pertinent pour vos objectifs.',
    qHealthIntensity: "Vous aurez des jalons d'entraînement qui soutiennent vos objectifs physiques et mentaux.",
    qHealthSnapshot: 'Voici votre plan SMART santé au club. Confirmez pour l’enregistrer.',
    healthDim_physical: 'Physique',
    healthDim_mental: 'Mental',
    healthDim_social: 'Social',
    healthWho_below: 'En dessous de la reco',
    healthWho_around: 'Autour de la reco',
    healthWho_above: 'Je la remplis déjà',
    healthDelivery_into_coaching: 'Dans l’entraînement club habituel',
    healthDelivery_separate_sessions: 'Séances santé dédiées',
    healthDelivery_both: 'Les deux',
    healthIntensity_regular: 'Intensité club habituelle',
    healthIntensity_gentle: 'Douce / faible impact',
    healthIntensity_adapted: 'Jeu adapté — le coach doit le savoir',
    healthSessions_1: '1 séance par semaine',
    healthSessions_2: '2 séances par semaine',
    'healthSessions_3+': '3 séances ou plus par semaine',
    healthSnapshotGoal: 'Spécifique',
    healthSnapshotTrack: 'Mesurable',
    healthSnapshotDoable: 'Atteignable',
    healthSnapshotWhy: 'Pertinent',
    healthSnapshotWhen: 'Temporel',
    healthSnapshotConfirm: 'C’est mon plan santé',
    healthContinue: 'Continuer',
    healthIntroTitle: 'La santé dans votre club',
    healthIntroText: 'Voici **Pingflo**. Nous transformons vos réponses santé en objectifs SMART de ping au club — bien-être physique, mental et social dans les séances du quotidien.',
    healthIntroAction: 'Prêt à définir votre parcours santé ?',
    healthStartBtn: 'Commencer le profilage santé',
    healthMindset: 'Vos séances de ping au club comptent pour votre plan santé. Pingflo rappelle la reco OMS, sans en faire un devoir.'
  },
  es: {
    healthSmartItems: [
      { letter: 'S', label: 'Específico', desc: 'Hacer crecer el bienestar físico, mental y/o social con el ping del club.' },
      { letter: 'M', label: 'Medible', desc: 'Minutos OMS para tu edad más las sesiones de club que puedas mantener. El ping cuenta para el total.' },
      { letter: 'A', label: 'Alcanzable', desc: 'Tendrás hitos de entrenamiento que apoyan tus objetivos físicos y mentales.' },
      { letter: 'R', label: 'Relevante', desc: 'Tu coach se asegurará de que el plan sea relevante para tus objetivos.' },
      { letter: 'T', label: 'Temporal', desc: 'Una temporada o una fase de 4 meses, luego revisión con el club.' }
    ],
    qTrainingVolumeHealth: '¿Cuántas sesiones de club por semana puedes mantener? El ping cuenta para el total OMS — no es un cupo solo de ping.',
    qHealthIntro: 'La salud en un club es bienestar físico, mental y social. El tenis de mesa es un marco natural Sports Club for Health — lo integramos en las sesiones, no al margen.',
    qHealthDimensions: '¿Qué dimensiones deben crecer para ti? Selecciona todas las que apliquen.',
    qHealthWhoCheck: 'Esta es la guía OMS para tu edad. Las sesiones del club cuentan. ¿Dónde estás hoy?',
    qHealthDelivery: 'Tu coach se asegurará de que el plan sea relevante para tus objetivos.',
    qHealthIntensity: 'Tendrás hitos de entrenamiento que apoyan tus objetivos físicos y mentales.',
    qHealthSnapshot: 'Este es tu plan SMART de salud en el club. Confirma para guardarlo.',
    healthDim_physical: 'Físico',
    healthDim_mental: 'Mental',
    healthDim_social: 'Social',
    healthWho_below: 'Por debajo de la guía',
    healthWho_around: 'En torno a la guía',
    healthWho_above: 'Ya la cumplo',
    healthDelivery_into_coaching: 'En el entrenamiento habitual del club',
    healthDelivery_separate_sessions: 'Sesiones de salud dedicadas',
    healthDelivery_both: 'Ambas',
    healthIntensity_regular: 'Intensidad habitual del club',
    healthIntensity_gentle: 'Suave / bajo impacto',
    healthIntensity_adapted: 'Juego adaptado — el entrenador debe saberlo',
    healthSessions_1: '1 sesión a la semana',
    healthSessions_2: '2 sesiones a la semana',
    'healthSessions_3+': '3 o más sesiones a la semana',
    healthSnapshotGoal: 'Específico',
    healthSnapshotTrack: 'Medible',
    healthSnapshotDoable: 'Alcanzable',
    healthSnapshotWhy: 'Relevante',
    healthSnapshotWhen: 'Temporal',
    healthSnapshotConfirm: 'Este es mi plan de salud',
    healthContinue: 'Continuar',
    healthIntroTitle: 'Salud en tu club',
    healthIntroText: 'Conoce a **Pingflo**. Convertiremos tus respuestas de salud en objetivos SMART de ping en el club — bienestar físico, mental y social en las sesiones diarias.',
    healthIntroAction: '¿Listo para definir tu camino de salud?',
    healthStartBtn: 'Empezar el perfilado de salud',
    healthMindset: 'Tus sesiones de ping en el club cuentan para tu plan de salud. Pingflo recuerda la guía OMS sin convertirla en deberes.'
  },
  de: {
    healthSmartItems: [
      { letter: 'S', label: 'Spezifisch', desc: 'Körperliches, mentales und/oder soziales Wohlbefinden durch Vereinstischtennis stärken.' },
      { letter: 'M', label: 'Messbar', desc: 'WHO-Minuten für dein Alter plus die Vereinseinheiten, die du halten kannst. Vereins-TT zählt zum Total.' },
      { letter: 'A', label: 'Erreichbar', desc: 'Du erhältst Trainingsmeilensteine, die deine körperlichen und mentalen Ziele unterstützen.' },
      { letter: 'R', label: 'Relevant', desc: 'Dein Coach stellt sicher, dass der Plan für deine Ziele relevant ist.' },
      { letter: 'T', label: 'Terminiert', desc: 'Eine Saison oder 4-Monats-Phase, dann Review mit dem Verein.' }
    ],
    qTrainingVolumeHealth: 'Wie viele Vereinseinheiten pro Woche kannst du halten? Tischtennis zählt zum WHO-Total — keine TT-only-Quote.',
    qHealthIntro: 'Gesundheit im Sportverein ist körperliches, mentales und soziales Wohlbefinden. Tischtennis ist ein natürlicher Sports-Club-for-Health-Rahmen — wir weben es in die Einheiten, nicht daneben.',
    qHealthDimensions: 'Welche Dimensionen sollen für dich wachsen? Wähle alle zutreffenden.',
    qHealthWhoCheck: 'Hier ist die WHO-Empfehlung für dein Alter. Vereinseinheiten zählen. Wo stehst du heute?',
    qHealthDelivery: 'Dein Coach stellt sicher, dass der Plan für deine Ziele relevant ist.',
    qHealthIntensity: 'Du erhältst Trainingsmeilensteine, die deine körperlichen und mentalen Ziele unterstützen.',
    qHealthSnapshot: 'Hier ist dein Gesundheits-SMART-Plan im Verein. Bestätige zum Speichern.',
    healthDim_physical: 'Körperlich',
    healthDim_mental: 'Mental',
    healthDim_social: 'Sozial',
    healthWho_below: 'Unter der Empfehlung',
    healthWho_around: 'Um die Empfehlung',
    healthWho_above: 'Ich erfülle sie bereits',
    healthDelivery_into_coaching: 'Im regulären Vereinstraining',
    healthDelivery_separate_sessions: 'Eigene gesundheitsfördernde Einheiten',
    healthDelivery_both: 'Beides',
    healthIntensity_regular: 'Normale Vereinsintensität',
    healthIntensity_gentle: 'Sanft / gelenkschonend',
    healthIntensity_adapted: 'Angepasstes Spiel — der Trainer sollte es wissen',
    healthSessions_1: '1 Einheit pro Woche',
    healthSessions_2: '2 Einheiten pro Woche',
    'healthSessions_3+': '3 oder mehr Einheiten pro Woche',
    healthSnapshotGoal: 'Spezifisch',
    healthSnapshotTrack: 'Messbar',
    healthSnapshotDoable: 'Erreichbar',
    healthSnapshotWhy: 'Relevant',
    healthSnapshotWhen: 'Terminiert',
    healthSnapshotConfirm: 'Das ist mein Gesundheitsplan',
    healthContinue: 'Weiter',
    healthIntroTitle: 'Gesundheit in deinem Verein',
    healthIntroText: 'Das ist **Pingflo**. Wir machen aus deinen Gesundheitsantworten SMART-Trainingsziele für Tischtennis im Verein — körperliches, mentales und soziales Wohlbefinden in den täglichen Einheiten.',
    healthIntroAction: 'Bereit, deinen Gesundheitspfad zu setzen?',
    healthStartBtn: 'Gesundheitsprofil starten',
    healthMindset: 'Deine Vereinseinheiten zählen zu deinem Gesundheitsplan. Pingflo erinnert an die WHO-Empfehlung, ohne Hausaufgaben daraus zu machen.'
  },
  nl: {
    achievableSummary: 'De coach beoordeelt je trainingsinspanningen ten opzichte van de verwachte resultaten',
    typing: 'Pingflo typt...',
    introTitle: 'SMART Training Tool',
    introText: 'Maak kennis met **Pingflo** — gastheer van het SMART-agententeam van je club (Pingwi, Pingpro, Pingmynd). Samen bouwen we je atletenprofiel. Om twee keer zo snel te groeien, gebruiken we de **SMART**-doelmethode:',
    digipingTag: 'SMART-Agent van je club',
    introAction: 'Laten we je mindset afstemmen! Druk hieronder om te starten:',
    startBtn: 'Start SMART-profilering',
    continueBtn: 'Ga verder met SMART-setup',
    joinClubBanner: 'Word lid van {club}',
    smartItems: [
      { letter: 'S', label: 'Specifiek', desc: 'Noem de precieze trainingsdoelen die er het meest toe doen — een ★-hoofdfocus en optionele ·-nevendoelen — zodat elke sessie een helder, concreet doel heeft richting je seizoensresultaat.' },
      { letter: 'M', label: 'Meetbaar', desc: 'Voortgang volgt via subjectieve en objectieve evaluaties: zelfevaluaties, opvolging door de coach, en oefenmetriek uit trainingsanalyse.' },
      { letter: 'A', label: 'Haalbaar', desc: 'Je coach beoordeelt je trainingsinspanningen ten opzichte van de verwachte resultaten — of de voortgang realistisch en op schema is.' },
      { letter: 'R', label: 'Relevant', desc: 'Het doel moet ertoe doen en passen bij je bredere plannen en doelstellingen.' },
      { letter: 'T', label: 'Tijdgebonden', desc: 'Twee klokken: een 3-wekensprint voor gepersonaliseerde oefeningen op je primaire en secundaire SMART-doelen, en een half seizoen aanwezigheid richting het seizoensresultaat.' }
    ],
    qLevel: 'Wat is je huidige officiële FLTT-klassement?',
    qLevelGeneric: 'Wat is je huidige officiële klassement?',
    qCustomLevel: 'Wat is je huidige officiële klassement in je land?',
    healthSmartItems: [
      { letter: 'S', label: 'Specifiek', desc: 'Fysiek, mentaal en/of sociaal welzijn laten groeien via clubtafeltennis.' },
      { letter: 'M', label: 'Meetbaar', desc: 'WHO-minuten voor jouw leeftijd plus de clubsessies die je volhoudt. Club-TT telt mee voor het totaal.' },
      { letter: 'A', label: 'Haalbaar', desc: 'Je krijgt trainingsmijlpalen die je fysieke en mentale doelen ondersteunen.' },
      { letter: 'R', label: 'Relevant', desc: 'Je coach zorgt dat het plan relevant is voor jouw doelen.' },
      { letter: 'T', label: 'Tijdgebonden', desc: 'Een seizoen of 4-maandenfase, daarna nabespreking met de club.' }
    ],
    qTrainingVolumeHealth: 'Hoeveel clubsessies per week kun je volhouden? Tafeltennis telt mee voor het WHO-totaal — geen TT-only quota.',
    qHealthIntro: 'Gezondheid in een sportclub is fysiek, mentaal en sociaal welzijn. Tafeltennis is een natuurlijke Sports Club for Health-omgeving — we weven het in de sessies, niet ernaast.',
    qHealthDimensions: 'Welke dimensies moeten voor jou groeien? Selecteer alles wat geldt.',
    qHealthWhoCheck: 'Hier is de WHO-richtlijn voor jouw leeftijd. Clubsessies tellen mee. Waar sta je vandaag?',
    qHealthDelivery: 'Je coach zorgt dat het plan relevant is voor jouw doelen.',
    qHealthIntensity: 'Je krijgt trainingsmijlpalen die je fysieke en mentale doelen ondersteunen.',
    qHealthSnapshot: 'Hier is je gezondheids-SMART-plan in de club. Bevestig om op te slaan.',
    healthDim_physical: 'Fysiek',
    healthDim_mental: 'Mentaal',
    healthDim_social: 'Sociaal',
    healthWho_below: 'Onder de richtlijn',
    healthWho_around: 'Rond de richtlijn',
    healthWho_above: 'Ik haal hem al',
    healthDelivery_into_coaching: 'In de gewone clubtraining',
    healthDelivery_separate_sessions: 'Aparte gezondheidssessies',
    healthDelivery_both: 'Beide',
    healthIntensity_regular: 'Gewone clubintensiteit',
    healthIntensity_gentle: 'Zacht / weinig impact',
    healthIntensity_adapted: 'Aangepast spel — de coach moet het weten',
    healthSessions_1: '1 sessie per week',
    healthSessions_2: '2 sessies per week',
    'healthSessions_3+': '3 of meer sessies per week',
    healthSnapshotGoal: 'Specifiek',
    healthSnapshotTrack: 'Meetbaar',
    healthSnapshotDoable: 'Haalbaar',
    healthSnapshotWhy: 'Relevant',
    healthSnapshotWhen: 'Tijdgebonden',
    healthSnapshotConfirm: 'Dit is mijn gezondheidsplan',
    healthContinue: 'Verder',
    enterToContinue: 'Druk op',
    healthIntroTitle: 'Gezondheid in je club',
    healthIntroText: 'Maak kennis met **Pingflo**. We zetten je gezondheidsantwoorden om in SMART-trainingsdoelen voor tafeltennis in de club — fysiek, mentaal en sociaal welzijn in de dagelijkse sessies.',
    healthIntroAction: 'Klaar om je gezondheidspad te zetten?',
    healthStartBtn: 'Start gezondheidsprofilering',
    healthMindset: 'Je clubsessies tellen mee voor je gezondheidsplan. Pingflo herinnert aan de WHO-richtlijn zonder er huiswerk van te maken.'
  },
  pt: {
    achievableSummary: 'O treinador avalia os teus esforços de treino face aos resultados esperados',
    joinClubBanner: 'Junta-te a {club}',
    healthSmartItems: [
      { letter: 'S', label: 'Específico', desc: 'Fazer crescer o bem-estar físico, mental e/ou social através do ténis de mesa no clube.' },
      { letter: 'M', label: 'Mensurável', desc: 'Minutos OMS para a sua idade mais as sessões de clube que consegue manter. O ping conta para o total.' },
      { letter: 'A', label: 'Alcançável', desc: 'Terás marcos de treino que apoiam os teus objetivos físicos e mentais.' },
      { letter: 'R', label: 'Relevante', desc: 'O teu treinador garante que o plano é relevante para os teus objetivos.' },
      { letter: 'T', label: 'Temporal', desc: 'Uma época ou fase de 4 meses, depois revisão com o clube.' }
    ],
    qTrainingVolumeHealth: 'Quantas sessões de clube por semana consegue manter? O ténis de mesa conta para o total OMS — não é um quota só de ping.',
    qHealthIntro: 'A saúde num clube é bem-estar físico, mental e social. O ténis de mesa é um contexto natural Sports Club for Health — integramos nas sessões, não à parte.',
    qHealthDimensions: 'Que dimensões devem crescer para si? Selecione todas as que se aplicam.',
    qHealthWhoCheck: 'Aqui está a recomendação OMS para a sua idade. As sessões do clube contam. Onde está hoje?',
    qHealthDelivery: 'O teu treinador garante que o plano é relevante para os teus objetivos.',
    qHealthIntensity: 'Terás marcos de treino que apoiam os teus objetivos físicos e mentais.',
    qHealthSnapshot: 'Aqui está o seu plano SMART de saúde no clube. Confirme para guardar.',
    healthDim_physical: 'Físico',
    healthDim_mental: 'Mental',
    healthDim_social: 'Social',
    healthWho_below: 'Abaixo da recomendação',
    healthWho_around: 'Perto da recomendação',
    healthWho_above: 'Já a cumpro',
    healthDelivery_into_coaching: 'No treino habitual do clube',
    healthDelivery_separate_sessions: 'Sessões de saúde dedicadas',
    healthDelivery_both: 'Ambos',
    healthIntensity_regular: 'Intensidade habitual do clube',
    healthIntensity_gentle: 'Suave / baixo impacto',
    healthIntensity_adapted: 'Jogo adaptado — o treinador deve saber',
    healthSessions_1: '1 sessão por semana',
    healthSessions_2: '2 sessões por semana',
    'healthSessions_3+': '3 ou mais sessões por semana',
    healthSnapshotGoal: 'Específico',
    healthSnapshotTrack: 'Mensurável',
    healthSnapshotDoable: 'Alcançável',
    healthSnapshotWhy: 'Relevante',
    healthSnapshotWhen: 'Temporal',
    healthSnapshotConfirm: 'Este é o meu plano de saúde',
    healthContinue: 'Continuar',
    enterToContinue: 'Prima',
    healthIntroTitle: 'Saúde no seu clube',
    healthIntroText: 'Conheça o **Pingflo**. Vamos transformar as suas respostas de saúde em objetivos SMART de ténis de mesa no clube — bem-estar físico, mental e social nas sessões do dia a dia.',
    healthIntroAction: 'Pronto para definir o seu percurso de saúde?',
    healthStartBtn: 'Começar o perfil de saúde',
    healthMindset: 'As suas sessões de clube contam para o seu plano de saúde. O Pingflo lembra a recomendação OMS sem transformar isso em trabalho de casa.'
  }
};

const AIProfilingWizard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { clubCode: paramClubCode } = useParams();
  const { brand, refreshBrand } = useBrand();
  useWizardViewportHeight();

  /** QR / public club-join signup — always ask motivation; ignore stale session purpose. */
  const isPublicClubJoin = Boolean(
    paramClubCode || searchParams.get('club_code')
  );

  // Determine active language dictionary
  const currentLang = useMemo(() => {
    const raw = (i18n.language || 'en').split('-')[0];
    const lang = raw === 'gr' || raw === 'ge' ? 'de' : raw === 'sp' ? 'es' : raw;
    return TRANSLATIONS[lang] ? lang : 'en';
  }, [i18n.language]);

  const trans = useMemo(
    () => ({
      ...TRANSLATIONS.en,
      ...(TRANSLATIONS[currentLang] || {}),
      ...(HEALTH_WIZARD_COPY[currentLang] || {})
    }),
    [currentLang]
  );
  const dimensionLabels = useMemo(() => getDimensionMeta(currentLang), [currentLang]);

  // Auth / invitation state
  const [user, setUser] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [joinClub, setJoinClub] = useState(null);
  const [isNewRegistration, setIsNewRegistration] = useState(true);
  /** Existing player re-doing Pingflo — password verifies identity before profile/SMART update */
  const [existingAccount, setExistingAccount] = useState(false);
  const existingAccountRef = useRef(false);
  const [emailChecking, setEmailChecking] = useState(false);

  const setExistingAccountFlag = useCallback((value) => {
    existingAccountRef.current = !!value;
    setExistingAccount(!!value);
  }, []);

  // Pingflo SMART state machine
  const [smart, setSmart] = useState(() => createInitialState());
  const smartRef = useRef(smart);
  smartRef.current = smart;
  const machineCtxRef = useRef({ invitation: null, hasUser: false });
  machineCtxRef.current = {
    invitation: {
      ...(invitationData || {}),
      ...(joinClub || {}),
      club_id: joinClub?.club_id || invitationData?.club_id,
      club_code: joinClub?.club_code || invitationData?.club_code,
      club_name: joinClub?.club_name || invitationData?.club_name,
      club_address: joinClub?.club_address || invitationData?.club_address,
      city: joinClub?.city || invitationData?.city,
      postal_code: joinClub?.postal_code || invitationData?.postal_code,
      club_country: joinClub?.country || invitationData?.club_country || invitationData?.country,
      country: joinClub?.country || invitationData?.country || null,
    },
    hasUser: !!user?.user_id
  };

  const dispatchSmart = useCallback((event) => {
    const nextState = transition(smartRef.current, event, machineCtxRef.current);
    smartRef.current = nextState;
    setSmart(nextState);
    return nextState;
  }, []);

  const formData = useMemo(() => slotsToFormData(smart.slots), [smart.slots]);
  const wizardStep = smart.captureStep;
  const introStarted = smart.phase !== PHASES.INTRO;
  /** Intro card: Pingflo letter-stream before Start is available. */
  const [introPhase, setIntroPhase] = useState('body'); // body | smart | action | ready
  const [introSmartIdx, setIntroSmartIdx] = useState(0);

  useEffect(() => {
    if (introStarted) return undefined;
    setIntroPhase('body');
    setIntroSmartIdx(0);
    return undefined;
  }, [currentLang, introStarted]);

  useEffect(() => {
    if (introPhase !== 'smart') return undefined;
    if ((trans.smartItems || []).length === 0) setIntroPhase('action');
    return undefined;
  }, [introPhase, trans.smartItems]);

  // APIs data state
  const [mainPurposes, setMainPurposes] = useState([]);
  const [experienceLevels, setExperienceLevels] = useState([]);
  const [playingStyles, setPlayingStyles] = useState([]);
  const [focusCategories, setFocusCategories] = useState([]);
  const [attributesByCategory, setAttributesByCategory] = useState({});
  const [levels, setLevels] = useState([]);
  const [luxembourgClubs, setLuxembourgClubs] = useState([]);
  const [lookupError, setLookupError] = useState(null);

  /** Offline / API-failure fallback so the purpose step is never empty. */
  const FALLBACK_MAIN_PURPOSES = useMemo(
    () => [
      {
        main_purpose_id: 1,
        main_purpose_name: 'Health',
        translations: [
          {
            language_code: currentLang,
            name:
              currentLang === 'fr'
                ? 'Santé'
                : currentLang === 'es'
                  ? 'Salud'
                  : currentLang === 'de'
                    ? 'Gesundheit'
                    : 'Health',
          },
        ],
      },
      {
        main_purpose_id: 2,
        main_purpose_name: 'Recreational',
        translations: [
          {
            language_code: currentLang,
            name:
              currentLang === 'fr'
                ? 'Récréatif'
                : currentLang === 'es'
                  ? 'Recreativo'
                  : currentLang === 'de'
                    ? 'Freizeit'
                    : 'Recreational',
          },
        ],
      },
      {
        main_purpose_id: 3,
        main_purpose_name: 'Competition',
        translations: [
          {
            language_code: currentLang,
            name:
              currentLang === 'fr'
                ? 'Compétition'
                : currentLang === 'es'
                  ? 'Competencia'
                  : currentLang === 'de'
                    ? 'Wettbewerb'
                    : 'Competition',
          },
        ],
      },
    ],
    [currentLang]
  );

  // Conversational Messages State
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  /** Message id currently being letter-streamed (hides options until done). */
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const messageAnchorByDim = useRef({});
  
  // Custom states for in-chat inputs
  const [countrySearch, setCountrySearch] = useState('');
  const [clubSearch, setClubSearch] = useState('');
  const [textInputValue, setTextInputValue] = useState('');
  const [textInputError, setTextInputError] = useState('');
  /** When set, user is correcting a past answer for this capture step. */
  const [editingStep, setEditingStep] = useState(null);
  
  // Two password fields
  const [passwordForm, setPasswordForm] = useState({ password: '', confirm: '' });
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);
  const [assessmentError, setAssessmentError] = useState('');
  /** Saved wizard draft detected on load — player can continue across sessions. */
  const [pendingResume, setPendingResume] = useState(null);
  const resumeCheckedRef = useRef(false);
  const profilingFinishedRef = useRef(false);

  const chatEndRef = useRef(null);
  const chatHistoryRef = useRef(null);

  // Keep the latest Pingflo question visible above the options tray
  const scrollQuestionIntoView = useCallback((behavior = 'smooth') => {
    const run = () => {
      const history = chatHistoryRef.current;
      if (!history) return;
      history.scrollTo({
        top: history.scrollHeight,
        behavior
      });
    };
    // Double rAF: wait for tray layout after streaming ends / options mount
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    setTimeout(run, 120);
    setTimeout(run, 320);
  }, []);

  useEffect(() => {
    scrollQuestionIntoView(streamingMsgId ? 'auto' : 'smooth');
  }, [messages, isTyping, wizardStep, streamingMsgId, editingStep, scrollQuestionIntoView]);

  // When letter-stream finishes, options appear and shrink the history — re-scroll
  useEffect(() => {
    if (streamingMsgId || isTyping) return undefined;
    scrollQuestionIntoView('smooth');
    return undefined;
  }, [streamingMsgId, isTyping, wizardStep, scrollQuestionIntoView]);

  // Clear boot overlay for /join links (esp. invitation_token) — same contract as ClubAdminProfiling.
  useEffect(() => {
    document.body.setAttribute("data-digiping-ready", "1");
    return () => document.body.removeAttribute("data-digiping-ready");
  }, []);

  // Load User / Invitation Token
  useEffect(() => {
    // Public QR join is a fresh signup path — do not adopt a prior session user
    // (that incorrectly forced Health and skipped motivation).
    const userData = !isPublicClubJoin ? localStorage.getItem('user') : null;
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsNewRegistration(false);
        console.log('👤 Stored user found:', parsedUser);
        if (parsedUser.fltt_classement || parsedUser.fltt_ranking_points != null) {
          dispatchSmart({
            type: 'PREFILL',
            payload: {
              customLevel: parsedUser.fltt_classement || '',
              classement: parsedUser.fltt_classement || '',
              rankingPoints: parsedUser.fltt_ranking_points ?? null
            }
          });
        }
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }

    const invitationToken = searchParams.get('invitation_token');
    const applyInvitation = (decoded) => {
      if (!decoded) return;
      setInvitationData((prev) => ({ ...(prev || {}), ...decoded }));
      machineCtxRef.current.invitation = { ...(machineCtxRef.current.invitation || {}), ...decoded };
      dispatchSmart({
        type: 'PREFILL',
        payload: {
          email: decoded.email || '',
          firstName: decoded.first_name || decoded.firstName || '',
          lastName: decoded.last_name || decoded.lastName || '',
          country: decoded.country || null,
          clubId: decoded.club_id || null,
          customLevel: decoded.fltt_classement || '',
          classement: decoded.fltt_classement || '',
          rankingPoints: decoded.fltt_ranking_points ?? null
        }
      });
    };

    if (invitationToken) {
      try {
        const base64Url = invitationToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = JSON.parse(atob(base64));
        console.log('📧 Decoded invitation:', decoded);
        applyInvitation(decoded);
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }

    const clubCode = paramClubCode || searchParams.get('club_code');
    if (clubCode) {
      axios
        .get(`${ip_back}api/club/join/${encodeURIComponent(clubCode)}`)
        .then((res) => {
          const club = res.data?.data;
          if (!club?.club_id) return;
          setJoinClub(club);
          try {
            localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, String(club.club_id));
          } catch (_) {
            /* ignore */
          }
          refreshBrand?.();
          applyInvitation({
            club_id: club.club_id,
            country: club.country || null,
            club_name: club.club_name,
            club_code: club.club_code,
            club_address: club.club_address,
            city: club.city,
            postal_code: club.postal_code,
            ...(invitationToken ? {} : { invitation_type: 'club_join' })
          });
        })
        .catch((err) => {
          console.error('Club join lookup failed:', err);
        });
    }

    // Load initial options
    loadLookupData();
  }, []);

  useEffect(() => {
    if (paramClubCode || searchParams.get('club_code') || joinClub?.club_id || invitationData?.club_id) {
      return;
    }
    const code = brand?.club_subdomain || brand?.club_id;
    if (!code) return;
    let cancelled = false;
    axios
      .get(`${ip_back}api/club/join/${encodeURIComponent(code)}`)
      .then((res) => {
        const club = res.data?.data;
        if (cancelled || !club?.club_id) return;
        setJoinClub(club);
        try {
          localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, String(club.club_id));
        } catch (_) {
          /* ignore */
        }
        setInvitationData((prev) => ({
          ...(prev || {}),
          club_id: club.club_id,
          country: club.country || prev?.country || null,
          club_name: club.club_name,
          club_code: club.club_code,
          club_address: club.club_address,
          city: club.city,
          postal_code: club.postal_code,
          invitation_type: prev?.invitation_type || 'club_join',
        }));
        machineCtxRef.current.invitation = {
          ...(machineCtxRef.current.invitation || {}),
          club_id: club.club_id,
          country: club.country || null,
          club_name: club.club_name,
          club_code: club.club_code,
          club_address: club.club_address,
          city: club.city,
          postal_code: club.postal_code,
          invitation_type: 'club_join',
        };
        dispatchSmart({
          type: 'PREFILL',
          payload: { country: club.country || null, clubId: club.club_id },
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [brand?.club_id, brand?.club_subdomain, paramClubCode, joinClub?.club_id, invitationData?.club_id, searchParams, dispatchSmart]);

  useEffect(() => {
    const club = joinClub || invitationData;
    if (!club?.club_id && !club?.club_code && !club?.country && !club?.club_address) return;
    applyInvitePageLanguage(i18n, {
      ...club,
      country: club.country || invitationData?.country,
      club_code: club.club_code || invitationData?.club_code,
      club_name: club.club_name || invitationData?.club_name,
      club_address: club.club_address || invitationData?.club_address,
      city: club.city || invitationData?.city,
      postal_code: club.postal_code || invitationData?.postal_code,
    });
  }, [joinClub, invitationData, i18n]);

  // Detect saved wizard draft (server or localStorage) so players can resume later
  useEffect(() => {
    if (resumeCheckedRef.current) return undefined;
    let cancelled = false;

    (async () => {
      const token = localStorage.getItem('token');
      const localDraft = loadWizardDraftLocal();
      let profile = null;

      if (token) {
        try {
          profile = await fetchSmartProfile(null, currentLang);
        } catch (err) {
          console.warn('SMART profile load for resume failed:', err?.message || err);
        }
      }

      if (cancelled) return;
      resumeCheckedRef.current = true;
      // Fresh QR join should not resume another device/session draft into Health.
      if (isPublicClubJoin) {
        setPendingResume(null);
        return;
      }
      try {
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        if (stored?.isProfileCompleted === true) {
          // Onboarding already done — do not offer resume into assessment again.
          setPendingResume(null);
          try {
            await clearWizardDraft({
              hasToken: Boolean(token),
              lang: currentLang,
            });
          } catch (_) {
            /* ignore */
          }
          return;
        }
      } catch (_) {
        /* ignore */
      }
      const draft = extractResumeDraft(profile, localDraft);
      if (draft) setPendingResume(draft);
    })();

    return () => {
      cancelled = true;
    };
  }, [currentLang, isPublicClubJoin]);

  // Auto-save wizard progress after each answer (debounced)
  useEffect(() => {
    if (!introStarted) return undefined;
    if (smart.phase === PHASES.ACTIVE) return undefined;
    if (isSubmittingProfile) return undefined;
    if (profilingFinishedRef.current) return undefined;

    const token = localStorage.getItem('token');
    const timer = setTimeout(() => {
      persistWizardDraft(smart, messages, {
        lang: currentLang,
        hasToken: Boolean(token)
      });
    }, 900);

    return () => clearTimeout(timer);
  }, [smart, messages, introStarted, currentLang, isSubmittingProfile]);

  const loadLookupData = async () => {
    try {
      setLookupError(null);
      const langHeaders = { 'Accept-Language': currentLang };
      const [purposesRes, expRes, stylesRes, catRes, clubsRes] = await Promise.all([
        axios.get(`${ip_back}api/profile/main-purposes`, { headers: langHeaders }),
        axios.get(`${ip_back}api/profile/experience-levels`, { headers: langHeaders }),
        axios.get(`${ip_back}api/profile/playing-styles`, { headers: langHeaders }),
        axios.get(`${ip_back}api/profile/focus-categories`, { headers: langHeaders }),
        axios.get(`${ip_back}api/superAdmin/public/getClubsByCountry/LU`)
      ]);

      const purposes = purposesRes.data?.data || purposesRes.data || [];
      setMainPurposes(Array.isArray(purposes) && purposes.length ? purposes : FALLBACK_MAIN_PURPOSES);
      setExperienceLevels(expRes.data?.data || []);
      setPlayingStyles(stylesRes.data?.data || []);
      setFocusCategories(catRes.data?.data || []);
      
      if (clubsRes.data.status === 'success') {
        const parsedClubs = clubsRes.data.data.map(c => ({
          id: c.club_id,
          name: c.club_name,
          city: c.city || c.club_name.replace(/^DT\s+/, '').replace(/-P\.$/, ''),
          country: c.country
        }));
        setLuxembourgClubs(parsedClubs);
      }

      // Default levels
      const defaultLevels = [
        { level_id: 'beginner', level_name: t('beginner') },
        { level_id: 'intermediate', level_name: t('intermediate') },
        { level_id: 'advanced', level_name: t('advanced') }
      ];
      setLevels(defaultLevels);

      // Attempt to load LU classement levels
      try {
        const levelsRes = await axios.get(`${ip_back}api/profile/levels`);
        if (levelsRes.data.data) {
          setLevels(levelsRes.data.data);
        }
      } catch (_) {}

    } catch (err) {
      console.error('Error loading lookup data:', err);
      setLookupError(err?.message || 'lookup_failed');
      setMainPurposes(FALLBACK_MAIN_PURPOSES);
    }
  };

  // Fetch Focus Attributes when category determined
  const loadAttributes = async (categoryId, experienceLevelId) => {
    try {
      const params = {};
      if (experienceLevelId != null && experienceLevelId !== '') {
        params.experienceLevelId = experienceLevelId;
      }
      const res = await axios.get(`${ip_back}api/profile/attributes/${categoryId}`, {
        headers: { 'Accept-Language': currentLang },
        params
      });
      setAttributesByCategory(prev => ({
        ...prev,
        [categoryId]: res.data.data || []
      }));
    } catch (e) {
      console.error('Error loading focus attributes:', e);
    }
  };

  const loadFocusAttributesForPurpose = async (purposeId, experienceLevelId) => {
    const categoryIds = focusCategoryIdsToLoad(purposeId);
    await Promise.all(
      categoryIds.map((categoryId) => loadAttributes(categoryId, experienceLevelId))
    );
  };

  const getVisibleFocusAttributes = (excludeAttributeId) => {
    let visible = visibleFocusAttributes(attributesByCategory, {
      purposeId: formData.mainPurposeId,
      experienceLevelId: formData.experienceLevelId,
      excludeAttributeId
    });
    if (isHealthPurpose(formData.mainPurposeId)) {
      // Keep dimension-relevant health focuses, and always offer leisure-style
      // options + Other so players are not stuck with only 1–2 choices.
      const allowed = new Set([
        ...healthFocusIdsForDimensions(smart.slots?.health?.healthDimensions, {
          intensity: smart.slots?.health?.intensity
        }),
        ...LEISURE_BEGINNER_VISIBLE_FOCUS_IDS,
        FOCUS_OTHER_ATTRIBUTE_ID
      ].map(Number));
      visible = visible.filter((a) => allowed.has(Number(a.attribute_id)));
    }
    return visible;
  };

  /** Resolve localized label for profile lookup rows (API uses translations[]). */
  const getOptionLabel = (option) => {
    if (!option || typeof option !== 'object') return '';
    let label = '';
    if (option.translations?.length > 0) {
      label = (
        option.translations[0].name ||
        option.main_purpose_name ||
        option.experience_level_name ||
        option.style_name ||
        option.attribute_name ||
        option.level_name ||
        ''
      );
    } else {
      label = (
        option.main_purpose_name ||
        option.experience_level_name ||
        option.style_name ||
        option.attribute_name ||
        option.level_name ||
        option.name ||
        ''
      );
    }
    return formatFrenchSentenceCase(label, currentLang);
  };

  const getPurposeIcon = (purposeId) => {
    switch (Number(purposeId)) {
      case 1:
        return <HealthAndSafetyIcon className="tray-icon" />; // Health
      case 2:
        return <GroupsIcon className="tray-icon" />; // Recreational
      case 3:
        return <EmojiEventsIcon className="tray-icon" />; // Competition
      default:
        return <SportsTennisIcon className="tray-icon" />;
    }
  };

  /** Digits-only DOB → DD/MM/YYYY as the user types (max 8 digits). */
  const formatDobInput = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const handleTextInputChange = (e) => {
    const raw = e.target.value;
    if (wizardStep === 'dob') {
      setTextInputValue(formatDobInput(raw));
      if (textInputError) setTextInputError('');
      return;
    }
    setTextInputValue(raw);
  };

  // Trigger conversational flow start after clicking the SMART begin button
  const startWizard = () => {
    setPendingResume(null);
    const stored = user || (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || 'null');
      } catch {
        return null;
      }
    })();
    // Club QR join must always ask motivation first — never jump to Health from a
    // leftover session profile.
    const healthFlag =
      !isPublicClubJoin &&
      Number(stored?.main_purpose_id) === 1 &&
      stored?.user_id;
    if (healthFlag && stored?.user_id) {
      dispatchSmart({
        type: 'BEGIN',
        payload: {
          purposeId: 1,
          label: trans.healthIntroTitle || 'Health',
          dateOfBirth: stored.user_date_birth || stored.dateBorn || null,
          gender: stored.user_sex || stored.sex || null,
          firstName: stored.user_first_name || stored.firstName || '',
          lastName: stored.user_last_name || stored.lastName || '',
          email: stored.user_email || stored.email || ''
        }
      });
      loadFocusAttributesForPurpose(1);
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const id = 'welcome';
        messageAnchorByDim.current.S = id;
        setMessages([
          {
            id,
            sender: 'ai',
            text: trans.qHealthIntro,
            dimension: 'S'
          }
        ]);
        setStreamingMsgId(id);
      }, 600);
      return;
    }
    dispatchSmart({ type: 'BEGIN' });
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const id = 'welcome';
      messageAnchorByDim.current.S = id;
      setMessages([
        {
          id,
          sender: 'ai',
          text: trans.qPurpose,
          dimension: 'S'
        }
      ]);
      setStreamingMsgId(id);
    }, 600);
  };

  const promptForStep = (step) => {
    if (step === 'trainingVolume') {
      const purposeId = smartRef.current.slots?.S?.purposeId;
      if (purposeId === 3) return trans.qTrainingVolumeCompetition;
      if (purposeId === 2) return trans.qTrainingVolumeRecreational;
      if (purposeId === 1) return trans.qTrainingVolumeHealth;
      return trans.qTrainingVolume;
    }
    if (step === 'primaryFocus' && isLeisurePurpose(smartRef.current.slots?.S?.purposeId)) {
      return trans.qPrimaryFocusLeisure || trans.qPrimaryFocus;
    }
    if (step === 'primaryFocus' && Number(smartRef.current.slots?.S?.purposeId) === 3) {
      return trans.qPrimaryFocusCompetition || trans.qPrimaryFocus;
    }
    if (step === 'secondaryFocus' && Number(smartRef.current.slots?.S?.purposeId) === 3) {
      return trans.qSecondaryFocusCompetition || trans.qSecondaryFocus;
    }
    if (step === 'funChallengePreview') {
      return trans.qFunChallenge;
    }
    if (step === 'cycleLength') {
      const leisureT = isLeisurePurpose(smartRef.current.slots?.S?.purposeId);
      if (leisureT) return trans.qCycleLengthLeisure || trans.qCycleLength;
    }
    if (step === 'password' && existingAccountRef.current) {
      return trans.qPasswordExisting;
    }
    if (step === 'relevanceWhy' && Number(smartRef.current.slots?.S?.purposeId) === 3) {
      return trans.qRelevanceCompetition || trans.qRelevance;
    }
    if (step === 'primaryFocus' && isHealthPurpose(smartRef.current.slots?.S?.purposeId)) {
      return trans.qPrimaryFocus;
    }
    if (step === 'level') {
      const invite = machineCtxRef.current.invitation || {};
      const invitation = {
        ...invite,
        ...(joinClub || {}),
        club_id: joinClub?.club_id || invite.club_id,
        club_code: joinClub?.club_code || invite.club_code,
        club_name: joinClub?.club_name || invite.club_name,
        club_address: joinClub?.club_address || invite.club_address,
        city: joinClub?.city || invite.city,
        postal_code: joinClub?.postal_code || invite.postal_code,
        club_country: joinClub?.country || invite.club_country || invite.country,
        country: joinClub?.country || invite.country || smartRef.current.slots?.A?.country || null
      };
      if (isLuxembourgClassementContext(smartRef.current.slots, invitation)) {
        return trans.qLevel;
      }
      return trans.qLevelGeneric || trans.qCustomLevel;
    }
    const key = STEP_PROMPT_KEY[step];
    return key ? (trans[key] || t(key)) : null;
  };

  // Pingflo reply (dots → letter stream). captureStep already advanced by machine.
  const aiReply = (replyText, dimension, delay = 600) => {
    if (!replyText) return;
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const id = `step_${Date.now()}`;
      if (dimension) messageAnchorByDim.current[dimension] = id;
      setMessages(prev => [
        ...prev,
        {
          id,
          sender: 'ai',
          text: replyText,
          dimension: dimension || null
        }
      ]);
      setStreamingMsgId(id);
    }, delay);
  };

  const resumeWizard = () => {
    if (!pendingResume) return;
    const draft = pendingResume;
    setPendingResume(null);

    const next = dispatchSmart({
      type: 'HYDRATE',
      payload: {
        phase: draft.phase,
        captureStep: draft.captureStep,
        slots: draft.slots,
        status: draft.status,
        activities: draft.activities,
        coachAgreement: draft.coachAgreement,
        activeDimension: draft.activeDimension
      }
    });

    const log = draft.messageLog || [];
    if (log.length) {
      setMessages(log);
      const last = log[log.length - 1];
      if (last?.sender === 'user') {
        setTimeout(() => {
          const reply = promptForStep(next.captureStep);
          if (reply) aiReply(reply, next.activeDimension);
        }, 400);
      }
      return;
    }

    const welcomeId = 'resume_welcome';
    const questionId = 'resume_question';
    const questionText = promptForStep(next.captureStep) || trans.qPurpose;
    messageAnchorByDim.current[next.activeDimension || 'S'] = questionId;
    setMessages([
      {
        id: welcomeId,
        sender: 'ai',
        text: trans.resumeWelcome,
        dimension: 'S',
        isSystem: true
      },
      {
        id: questionId,
        sender: 'ai',
        text: questionText,
        dimension: next.activeDimension || 'S'
      }
    ]);
    setStreamingMsgId(questionId);
  };

  const pushUserMessage = (text, step) => {
    setMessages(prev => [
      ...prev,
      {
        id: `user_${Date.now()}`,
        sender: 'user',
        text,
        step: step || null,
        dimension: step ? STEP_TO_DIMENSION[step] || null : null
      }
    ]);
  };

  const isoToDobInput = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return '';
    const [y, m, d] = iso.slice(0, 10).split('-');
    return formatDobInput(`${d}${m}${y}`);
  };

  const prefillTextForStep = (step, slots) => {
    switch (step) {
      case 'customLevel':
        return slots.M?.customLevel || '';
      case 'dob':
        return isoToDobInput(slots.R?.dateOfBirth);
      case 'firstName':
        return slots.R?.firstName || '';
      case 'lastName':
        return slots.R?.lastName && String(slots.R.lastName).toLowerCase() !== 'user'
          ? slots.R.lastName
          : '';
      case 'name':
        return slots.R?.firstName || '';
      case 'email':
        return slots.R?.email || '';
      default:
        return '';
    }
  };

  /** Re-open a past question: truncate chat from that answer and clear downstream slots. */
  const beginEditAnswer = (msgId, step) => {
    const reopenStep = step === 'name' ? 'firstName' : step;
    if (!reopenStep || isTyping || streamingMsgId) return;
    if (reopenStep === 'assessment' || reopenStep === 'password') {
      // password can still be edited
      if (reopenStep === 'assessment') return;
    }

    const prevSlots = smartRef.current.slots;
    const draft = prefillTextForStep(reopenStep, prevSlots);

    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === msgId);
      if (idx < 0) return prev;
      return prev.slice(0, idx);
    });

    dispatchSmart({ type: 'REOPEN_STEP', payload: { step: reopenStep } });
    setEditingStep(reopenStep);
    setTextInputError('');
    setPasswordForm({ password: '', confirm: '' });
    if (reopenStep === 'email' || reopenStep === 'password') {
      setExistingAccountFlag(false);
    }
    setTextInputValue(draft);
    setCountrySearch('');
    setClubSearch('');

    setTimeout(() => {
      scrollQuestionIntoView('smooth');
    }, 80);
  };

  const applyAnswer = (event, userLabel) => {
    const answeredStep = smartRef.current.captureStep;
    pushUserMessage(userLabel, answeredStep);
    setEditingStep(null);
    const next = dispatchSmart(event);
    if (!next) return;
    if (next.phase === PHASES.ASSESSMENT || next.captureStep === 'assessment') {
      return;
    }
    const reply = promptForStep(next.captureStep);
    aiReply(reply, next.activeDimension);
  };

  // Handles clicking a standard multiple-choice option card
  const handleSelectOption = (stepId, valueId, labelText, rawValue) => {
    switch (stepId) {
      case 'purpose': {
        const purposeId = rawValue.main_purpose_id ?? rawValue.purpose_id ?? valueId;
        loadFocusAttributesForPurpose(purposeId);
        applyAnswer(
          { type: 'ANSWER_PURPOSE', payload: { purposeId, label: labelText } },
          labelText
        );
        break;
      }
      case 'experience':
        loadFocusAttributesForPurpose(formData.mainPurposeId, valueId);
        applyAnswer(
          { type: 'ANSWER_EXPERIENCE', payload: { experienceLevelId: valueId, label: labelText, lang: currentLang } },
          labelText
        );
        break;
      case 'competitionYears':
        applyAnswer(
          {
            type: 'ANSWER_COMPETITION_YEARS',
            payload: {
              band: rawValue?.band || valueId,
              years: rawValue?.years,
              label: labelText
            }
          },
          labelText
        );
        break;
      case 'style':
        applyAnswer(
          { type: 'ANSWER_STYLE', payload: { styleId: valueId, label: labelText } },
          labelText
        );
        break;
      case 'primaryFocus':
        applyAnswer(
          {
            type: 'ANSWER_PRIMARY_FOCUS',
            payload: {
              primaryFocusId: valueId,
              label: labelText,
              lang: currentLang
            }
          },
          labelText
        );
        break;
      case 'grip':
        applyAnswer(
          { type: 'ANSWER_GRIP', payload: { grip: valueId, label: labelText } },
          labelText
        );
        break;
      case 'hand':
        applyAnswer(
          { type: 'ANSWER_HAND', payload: { hand: valueId, label: labelText } },
          labelText
        );
        break;
      case 'gender':
        applyAnswer(
          { type: 'ANSWER_GENDER', payload: { gender: valueId, label: labelText } },
          labelText
        );
        break;
      case 'healthDimensions':
      case 'whoSelfCheck':
      case 'healthDelivery':
      case 'healthIntensity':
      case 'healthVolume':
        break;
      default:
        break;
    }
  };

  // Handles focus multi-select submission (max 2 secondary objectives)
  const handleConfirmSecondaryFocus = (selectedIds) => {
    const capped = (Array.isArray(selectedIds) ? selectedIds : []).slice(0, 2);
    const visible = getVisibleFocusAttributes(formData.primaryFocusId);
    const labels = capped.map(id => {
      const attr = visible.find(a => a.attribute_id === id);
      return attr ? (getOptionLabel(attr) || attr.attribute_name || id) : id;
    });
    const labelText = labels.join(', ') || 'None';
    applyAnswer(
      {
        type: 'ANSWER_SECONDARY_FOCUS',
        payload: { secondaryFocusIds: capped, label: labelText }
      },
      labelText
    );
  };

  // Handles Country Selection
  const handleSelectCountry = (countryCode, countryName) => {
    applyAnswer(
      { type: 'ANSWER_COUNTRY', payload: { country: countryCode, label: countryName } },
      countryName
    );
  };

  // Handles Club Selection
  const handleSelectClub = (clubId, clubName) => {
    applyAnswer(
      { type: 'ANSWER_CLUB', payload: { clubId, label: clubName } },
      clubName
    );
  };

  // Handles Level (Classement) Selection
  const handleSelectLevel = (levelId, levelName) => {
    applyAnswer(
      { type: 'ANSWER_LEVEL', payload: { levelId, label: levelName } },
      levelName
    );
  };

  const handleConfirmTrainingVolume = ({ sessionsPerWeek, hoursPerSession }) => {
    const sessionsLabel = `${sessionsPerWeek} ${trans.sessionsUnit}`;
    const hoursLabel = formatHoursPerSessionLabel(hoursPerSession, trans.hoursUnit);
    const label = `${sessionsLabel} · ${hoursLabel}`;
    const weeklyMinutes = estimateWeeklyTrainingMinutes(sessionsPerWeek, hoursPerSession);
    const weeklyHours = Math.round((weeklyMinutes / 60) * 10) / 10;
    const tipTemplate =
      weeklyMinutes >= WHO_WEEKLY_ACTIVITY_MINUTES
        ? trans.trainingVolumeHealthOk
        : trans.trainingVolumeHealthLow;
    const healthTip = String(tipTemplate || '')
      .replace(/\{minutes\}/g, String(weeklyMinutes))
      .replace(/\{hours\}/g, String(weeklyHours));

    const answeredStep = smartRef.current.captureStep;
    pushUserMessage(label, answeredStep);
    setEditingStep(null);
    const next = dispatchSmart({
      type: 'ANSWER_TRAINING_VOLUME',
      payload: { sessionsPerWeek, hoursPerSession, label }
    });
    if (!next) return;
    if (next.phase === PHASES.ASSESSMENT || next.captureStep === 'assessment') {
      return;
    }
    const nextPrompt = promptForStep(next.captureStep);
    const reply = [healthTip, nextPrompt].filter(Boolean).join('\n\n');
    aiReply(reply, next.activeDimension);
  };

  const handleConfirmFinalObjective = ({ kind, kinds, customText, label, resourceNote }) => {
    applyAnswer(
      {
        type: 'ANSWER_FINAL_OBJECTIVE',
        payload: { kind, kinds, customText, label, resourceNote }
      },
      label
    );
  };

  const handleConfirmMilestones = ({ milestones, resourceNote }) => {
    const label = milestones.map((m) => m.label).filter(Boolean).join(' · ');
    const display = [label, resourceNote].filter(Boolean).join(' · ');
    applyAnswer(
      { type: 'ANSWER_MILESTONES', payload: { milestones, resourceNote } },
      display || label
    );
  };

  const handleConfirmRelevance = (whyItMatters) => {
    applyAnswer(
      { type: 'ANSWER_RELEVANCE', payload: { whyItMatters, label: whyItMatters } },
      whyItMatters
    );
  };

  const handleConfirmFunChallenge = () => {
    applyAnswer({ type: 'CONFIRM_FUN_CHALLENGE', payload: {} }, trans.funChallengeConfirm || 'Let\'s go!');
  };

  const handleConfirmCycleLength = (weeks = DEFAULT_TECHNICAL_SPRINT_WEEKS, labelOverride) => {
    const w = Number(weeks) > 0 ? Number(weeks) : DEFAULT_TECHNICAL_SPRINT_WEEKS;
    const label =
      labelOverride ||
      (trans.cycleWeeksConfirm || trans.cycleWeeks3 || `${w}-week technical sprint`) +
        (trans.cycleWeeksHint ? ` · ${trans.cycleWeeksHint}` : '');
    applyAnswer(
      {
        type: 'ANSWER_CYCLE_LENGTH',
        payload: { cycleWeeks: w, label }
      },
      label
    );
  };

  // Universal handler for text input screens (Custom Level, DOB, Names, Email, Password)
  const handleTextSubmit = async (e) => {
    e.preventDefault();
    setTextInputError('');

    const val = textInputValue.trim();
    if (!val && wizardStep !== 'customLevel') return;

    if (wizardStep === 'dob') {
      const dobRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      if (!dobRegex.test(val)) {
        setTextInputError(trans.dobError);
        return;
      }
    }

    if (wizardStep === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(val)) {
        setTextInputError(trans.emailError);
        return;
      }

      setEmailChecking(true);
      let isExisting = false;
      try {
        const existingRes = await axios.post(`${ip_back}api/user/getUserByEmail`, {
          email: val.toLowerCase()
        });
        if (existingRes.data?.status === 'success' && existingRes.data?.data) {
          const u = existingRes.data.data;
          const playerRows = u.tbl_player || u.dataValues?.tbl_player || [];
          const hasPlayer =
            u.type_user_id === 4 ||
            (Array.isArray(playerRows) && playerRows.length > 0);
          // Admin-only accounts can still create a player role (new password flow)
          isExisting = !!hasPlayer;
        }
      } catch (_) {
        isExisting = false;
      } finally {
        setEmailChecking(false);
      }

      setExistingAccountFlag(isExisting);
      setTextInputValue('');
      applyAnswer(
        { type: 'ANSWER_EMAIL', payload: { email: val, label: val } },
        val
      );
      return;
    }

    if (wizardStep === 'firstName' || wizardStep === 'lastName' || wizardStep === 'name') {
      if (!val) {
        setTextInputError(
          wizardStep === 'lastName'
            ? (t('lastNameRequired') || 'Last name is required')
            : (t('firstNameRequired') || 'First name is required')
        );
        return;
      }
    }

    switch (wizardStep) {
      case 'customLevel':
        setTextInputValue('');
        applyAnswer(
          { type: 'ANSWER_CUSTOM_LEVEL', payload: { customLevel: val, label: val || 'Skipped' } },
          val || 'Skipped'
        );
        break;

      case 'dob': {
        const parts = val.split('/');
        const formattedDate =
          parseDateOfBirth(val) ||
          `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        setTextInputValue('');
        applyAnswer(
          { type: 'ANSWER_DOB', payload: { dateOfBirth: formattedDate, label: val } },
          val
        );
        break;
      }

      case 'firstName':
      case 'name':
        setTextInputValue('');
        applyAnswer(
          {
            type: 'ANSWER_FIRST_NAME',
            payload: { firstName: val, label: val }
          },
          val
        );
        break;

      case 'lastName':
        setTextInputValue('');
        applyAnswer(
          {
            type: 'ANSWER_LAST_NAME',
            payload: { lastName: val, label: val }
          },
          val
        );
        break;

      default:
        break;
    }
  };

  // Secure password submission (create new OR verify existing account)
  const handlePasswordFormSubmit = async (e) => {
    e.preventDefault();
    setTextInputError('');

    if (existingAccountRef.current) {
      if (!passwordForm.password) {
        setTextInputError(trans.passWrongError);
        return;
      }
      const emailToAuth =
        smartRef.current.slots?.R?.email ||
        formData.email ||
        localStorage.getItem('pendingEmail');
      try {
        setEmailChecking(true);
        const loginResponse = await axios.post(`${ip_back}api/user/login`, {
          email: emailToAuth,
          password: passwordForm.password
        });
        if (loginResponse.data.status !== 'success' || !loginResponse.data.data) {
          setTextInputError(trans.passWrongError);
          return;
        }
        const loggedIn = loginResponse.data.data;
        setUser(loggedIn);
        setIsNewRegistration(false);
        try {
          localStorage.setItem('home_active_role', 'player');
        } catch (_) {
          /* ignore */
        }
        localStorage.setItem('user', JSON.stringify(loggedIn));
        if (loggedIn.user_token) {
          localStorage.setItem('token', loggedIn.user_token);
        }
        applyAnswer(
          { type: 'ANSWER_PASSWORD', payload: { password: passwordForm.password } },
          '••••••••'
        );
      } catch (_) {
        setTextInputError(trans.passWrongError);
      } finally {
        setEmailChecking(false);
      }
      return;
    }

    if (passwordForm.password.length < 6) {
      setTextInputError(trans.passError);
      return;
    }
    if (passwordForm.password !== passwordForm.confirm) {
      setTextInputError(trans.passMatchError);
      return;
    }

    applyAnswer(
      { type: 'ANSWER_PASSWORD', payload: { password: passwordForm.password } },
      '••••••••'
    );
  };

  const smartSnapshotRows = useMemo(() => {
    const rows = buildSmartSnapshot(smart.slots, smart.status, dimensionLabels, smart.coachAgreement, {
      coachPendingNote: trans.coachPendingNote,
      coachMayRefine: trans.coachMayRefine,
      achievableSummary: trans.achievableSummary
    });
    return rows.map((row) => {
      if (row.dimension === 'T' && (!row.summary || row.summary === '—')) {
        return { ...row, summary: trans.cycleLabel };
      }
      return row;
    });
  }, [
    smart.slots,
    smart.status,
    smart.coachAgreement,
    dimensionLabels,
    trans.cycleLabel,
    trans.coachPendingNote,
    trans.coachMayRefine,
    trans.achievableSummary
  ]);

  // Ultimate Final submission handler
  const handleCompleteProfiling = async () => {
    if (isSubmittingProfile) return;
    dispatchSmart({ type: 'COMMIT_PROFILE' });
    setIsSubmittingProfile(true);
    setAssessmentError('');
    setMessages(prev => [
      ...prev,
      {
        id: `saving_${Date.now()}`,
        sender: 'ai',
        text:
          existingAccountRef.current || (user?.user_id && !isNewRegistration)
            ? trans.savingUpdate || trans.saving
            : trans.saving,
        isSystem: true
      }
    ]);

    try {
      let finalUser = user;
      const emailConfirmationToken = localStorage.getItem('emailConfirmationToken');
      const emailToUse = [
        formData.email,
        smartRef.current?.slots?.R?.email,
        localStorage.getItem('pendingEmail'),
        invitationData?.email,
        user?.user_email,
        user?.email,
      ]
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .find(Boolean) || '';
      const firstNameFallback =
        formData.firstName ||
        (emailToUse.includes('@') ? emailToUse.split('@')[0] : '') ||
        user?.user_first_name ||
        user?.first_name ||
        'Player';
      const isUpdatingExisting = existingAccountRef.current || (!!user?.user_id && !isNewRegistration);

      // 1. Account Creation (if new registration) — or login if email already exists
      const invitationToken = searchParams.get('invitation_token');
      const isPersonalInvite = Boolean(invitationToken);

      const birthDate = parseDateOfBirth(formData.dateOfBirth);

      if (isNewRegistration && !user?.user_id) {
        if (!emailToUse) {
          throw new Error(trans.emailRequired || 'Email is required to complete registration.');
        }
        if (isPersonalInvite) {
          const invitedPayload = {
            email: emailToUse,
            password: formData.password,
            firstName: firstNameFallback,
            lastName: formData.lastName || '',
            sex: formData.gender || 'male',
            gender: formData.gender || 'male',
            dateOfBirth: birthDate,
            clubId: formData.clubId || invitationData?.club_id,
            country: formData.country || invitationData?.country,
            mainPurposeId: formData.mainPurposeId,
            experienceLevelId: formData.experienceLevelId,
            styleId: formData.styleId,
            primaryFocusId: formData.primaryFocusId,
            secondaryFocusIds: formData.secondaryFocusIds,
            primaryFocusCustomText: formData.primaryFocusCustomText,
            secondaryFocusCustomText: formData.secondaryFocusCustomText,
            levelId: formData.levelId,
            customLevel: formData.customLevel,
            grip: formData.grip,
            hand: formData.hand,
            invitationToken
          };
          const invitedRes = await axios.post(
            `${ip_back}api/user/completeInvitedRegistration`,
            invitedPayload
          );
          if (invitedRes.data.status !== 'success') {
            throw new Error(invitedRes.data.message || 'Registration failed');
          }
          finalUser = invitedRes.data.data;
          if (finalUser?.token && !finalUser.user_token) {
            finalUser.user_token = finalUser.token;
          }
        } else {
        const registerData = {
          email: emailToUse,
          password: formData.password,
          firstName: firstNameFallback,
          lastName: formData.lastName || '',
          sex: formData.gender || 'male',
          typeUser_id: 4, // Player (1=SuperAdmin, 2=Admin, 3=Coach, 4=Player)
          confirmation_token: emailConfirmationToken || 'direct_registration',
          club_id: formData.clubId || invitationData?.club_id
        };

        if (birthDate) {
          registerData.dateBorn = birthDate;
        }

        console.log('📤 Submitting registration payload:', registerData);
        try {
          const registerResponse = await axios.post(`${ip_back}api/user/registerUser`, registerData);
          if (registerResponse.data.status !== 'success') {
            throw new Error(registerResponse.data.message || 'Registration failed');
          }

          finalUser = registerResponse.data.data;
        } catch (registerError) {
          const errMsg =
            registerError?.response?.data?.message || registerError?.message || '';
          const accountExists = /already registered/i.test(errMsg);
          if (accountExists) {
            console.log('👤 Account already exists — signing in to update profile & SMART');
            const loginResponse = await axios.post(`${ip_back}api/user/login`, {
              email: emailToUse,
              password: formData.password
            });
            if (loginResponse.data.status !== 'success' || !loginResponse.data.data) {
              throw new Error(trans.passWrongError || 'Incorrect password. Please try again.');
            }
            finalUser = loginResponse.data.data;
            setExistingAccountFlag(true);
          } else {
            throw registerError;
          }
        }
        }

        try {
          localStorage.setItem('home_active_role', 'player');
        } catch (_) {
          /* ignore */
        }
        if (finalUser?.token && !finalUser.user_token) {
          finalUser.user_token = finalUser.token;
        }
        localStorage.setItem('user', JSON.stringify(finalUser));
        if (finalUser.user_token) {
          localStorage.setItem('token', finalUser.user_token);
        }
        localStorage.removeItem('emailConfirmationToken');
        localStorage.removeItem('pendingEmail');
        localStorage.removeItem('selectedUserType');
      } else if (user?.user_id) {
        finalUser = user;
      }

      if (!finalUser?.user_id) {
        throw new Error(
          trans.registrationIncomplete ||
            'Could not create or load your account. Please check your email and try again.'
        );
      }

      // 2. Profile Completion / update
      const profilePayload = {
        userId: finalUser.user_id,
        mainPurposeId: formData.mainPurposeId,
        experienceLevelId: formData.experienceLevelId,
        styleId: formData.styleId,
        primaryFocusId: formData.primaryFocusId,
        secondaryFocusIds: formData.secondaryFocusIds,
        primaryFocusCustomText: formData.primaryFocusCustomText,
        secondaryFocusCustomText: formData.secondaryFocusCustomText,
        levelId: formData.levelId,
        customLevel: formData.customLevel,
        country: formData.country || invitationData?.country,
        clubId: formData.clubId || invitationData?.club_id,
        grip: formData.grip,
        hand: formData.hand,
        gender: formData.gender,
        dateOfBirth: birthDate,
        firstName:
          formData.firstName ||
          finalUser.first_name ||
          finalUser.user_first_name ||
          firstNameFallback,
        lastName:
          formData.lastName ||
          finalUser.last_name ||
          finalUser.user_last_name ||
          '',
        userLang: currentLang,
        healthProfile: isHealthPurpose(formData.mainPurposeId)
          ? {
              ...(formData.healthProfile || smartRef.current?.slots?.health || {}),
              primaryFocusId: formData.primaryFocusId,
              secondaryFocusId: (formData.secondaryFocusIds || [])[0] || null,
              sessionsPerWeek: smartRef.current?.slots?.M?.weeklyTraining?.sessionsPerWeek,
              experienceLevelId: formData.experienceLevelId,
            }
          : undefined
      };

      console.log('📤 Submitting profile payload:', profilePayload);
      const profileResponse = await axios.post(`${ip_back}api/profile/complete`, profilePayload);
      if (!profileResponse.data.success) {
        throw new Error(
          profileResponse.data.details ||
            profileResponse.data.error ||
            'Profile completion failed'
        );
      }

      const authToken =
        finalUser.user_token ||
        finalUser.token ||
        localStorage.getItem('token') ||
        null;
      const completeUserData = {
        ...finalUser,
        ...profileResponse.data.data,
        isProfileCompleted: true,
        user_token: authToken || profileResponse.data.data?.user_token,
        all_roles: profileResponse.data.data?.all_roles || finalUser?.all_roles,
        all_role_names: profileResponse.data.data?.all_role_names || finalUser?.all_role_names,
        hasCoachRole: profileResponse.data.data?.hasCoachRole ?? finalUser?.hasCoachRole,
        hasAdminRole: profileResponse.data.data?.hasAdminRole ?? finalUser?.hasAdminRole,
        hasPlayerRole: true,
        type_user_id: profileResponse.data.data?.type_user_id ?? finalUser?.type_user_id,
      };
      localStorage.setItem('user', JSON.stringify(completeUserData));
      if (completeUserData.user_token) {
        localStorage.setItem('token', completeUserData.user_token);
      }
      applyAppLanguage(i18n, currentLang);
      localStorage.removeItem('profilingFormData');
      localStorage.removeItem('healthOnboarding');
      localStorage.removeItem('pendingPlayerPurpose');

      // Refresh from getProfile so grip/hand/level/style match server (same shape as Profile page)
      try {
        const token = completeUserData.user_token || finalUser.user_token;
        if (token) {
          const clubId = String(
            completeUserData.club_id ||
              completeUserData.club_details?.club_id ||
              completeUserData.club?.club_id ||
              ""
          ).trim();
          const fresh = await axios.get(`${ip_back}api/user/getProfile`, {
            headers: {
              Authorization: token,
              'user-id': completeUserData.user_id,
              'Accept-Language': currentLang || 'en',
              'Content-Type': 'application/json'
            },
            params: clubId ? { club_id: clubId } : undefined,
          });
          if (fresh.data?.status === 'success' && fresh.data.data) {
            const merged = {
              ...fresh.data.data,
              isProfileCompleted: true,
              user_token: token,
              user_lang: currentLang,
            };
            localStorage.setItem('user', JSON.stringify(merged));
            localStorage.setItem('token', token);
            applyAppLanguage(i18n, currentLang);
          }
        }
      } catch (refreshErr) {
        console.warn('Profile refresh after Pingflo complete failed:', refreshErr?.message || refreshErr);
      }
      try {
        const snap = smartRef.current;
        localStorage.setItem(
          'digipingSmart',
          JSON.stringify({
            slots: snap.slots,
            activities: snap.activities,
            status: snap.status,
            coachAgreement: snap.coachAgreement
          })
        );
        // Persist SMART path for Pingflo agent + coach validation
        const token =
          localStorage.getItem('token') ||
          completeUserData?.user_token ||
          finalUser?.user_token;
        try {
          if (token) {
            localStorage.setItem('token', token);
            await axios.put(
              `${ip_back}api/digiping/smart`,
              {
                slots: snap.slots,
                activities: snap.activities,
                status: snap.status,
                coachAgreement: snap.coachAgreement,
                activeDimension: snap.activeDimension || 'S',
                phase: 'active',
                lang: (i18n.language || 'en').substring(0, 2)
              },
              { headers: { Authorization: token } }
            );
          } else {
            console.warn('Pingflo SMART persist skipped: no auth token');
          }
        } catch (smartErr) {
          console.warn('Pingflo SMART persist failed:', smartErr?.message || smartErr);
        }
        try {
          await clearWizardDraft({
            hasToken: Boolean(token),
            lang: (i18n.language || 'en').substring(0, 2)
          });
        } catch (_) {
          /* ignore */
        }
      } catch (_) {
        /* ignore quota / private mode */
      }

      // Sync brand context dynamically!
      await syncBrandAfterAuth(completeUserData, refreshBrand, { clubIdHint: formData.clubId });

      profilingFinishedRef.current = true;

      const successText = isUpdatingExisting || existingAccountRef.current
        ? trans.successUpdate || trans.success
        : trans.success;
      const verifyHint =
        isUpdatingExisting || existingAccountRef.current || invitationToken
          ? ''
          : `\n\n${trans.emailVerifyHint || ''}`;

      setMessages(prev => [
        ...prev,
        {
          id: `success_${Date.now()}`,
          sender: 'ai',
          text: `🎉 ${successText}${verifyHint}\n\n${trans.redirecting}`,
          isSystem: true
        }
      ]);

      setTimeout(() => {
        setIsSubmittingProfile(false);
        if (invitationData) {
          // If invited, redirect to login
          localStorage.setItem('invitedUserEmail', emailToUse);
          navigate('/login', {
            replace: true,
            state: {
              email: emailToUse,
              message: 'Registration complete! Please login with your credentials.'
            }
          });
        } else {
          // Land on Home; Pingflo auto-opens only if technical or SMART exercise setup is still pending.
          navigate('/home', { replace: true });
        }
      }, 2000);

    } catch (e) {
      console.error('Submission failed:', e);
      const apiMsg =
        e?.response?.data?.details ||
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Verification or profile completion failed. Please try again.';
      setIsSubmittingProfile(false);
      setAssessmentError(apiMsg);
      setMessages(prev => [
        ...prev,
        {
          id: `error_${Date.now()}`,
          sender: 'ai',
          text: `❌ Error: ${apiMsg}`
        }
      ]);
    }
  };

  // Render option nodes below active conversational flow
  const renderInteractiveTray = () => {
    // Keep the final assessment tray mounted while saving so the mindset text
    // does not remount/replay when completeProfile fails and retries.
    if (wizardStep !== 'assessment' && (isTyping || streamingMsgId)) return null;

    switch (wizardStep) {
      case 'purpose': {
        const purposes =
          Array.isArray(mainPurposes) && mainPurposes.length
            ? mainPurposes
            : FALLBACK_MAIN_PURPOSES;
        return (
          <div className="options-tray animate-slide-up">
            {lookupError ? (
              <button
                type="button"
                className="tray-card ripple"
                onClick={() => loadLookupData()}
              >
                <span className="tray-card-title">
                  {trans.retryLoadOptions || 'Retry loading options'}
                </span>
              </button>
            ) : null}
            {purposes.map(purpose => {
              const purposeId = purpose.main_purpose_id ?? purpose.purpose_id;
              const label = getOptionLabel(purpose);
              return (
                <button
                  key={purposeId}
                  type="button"
                  onClick={() => handleSelectOption('purpose', purposeId, label, purpose)}
                  className="tray-card ripple"
                >
                  {getPurposeIcon(purposeId)}
                  <span className="tray-card-title">{label}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case 'experience': {
        const isLeisureExp = formData.mainPurposeId === PURPOSE_RECREATIONAL
          || Number(smart.slots?.S?.purposeId) === PURPOSE_RECREATIONAL;
        const isHealthExp = isHealthPurpose(formData.mainPurposeId)
          || isHealthPurpose(smart.slots?.S?.purposeId);
        return (
          <div className="options-tray animate-slide-up">
            {experienceLevels.map(exp => {
              const id = exp.experience_level_id;
              const leisureLabel = isLeisureExp ? leisureExperienceLabel(id, currentLang) : '';
              const healthLabel = isHealthExp ? healthExperienceLabel(id, currentLang) : '';
              const label = formatFrenchSentenceCase(healthLabel || leisureLabel || getOptionLabel(exp), currentLang);
              return (
              <button
                key={id}
                type="button"
                onClick={() => handleSelectOption('experience', id, label, exp)}
                className="tray-card ripple flex-center"
              >
                <StarsIcon className="tray-icon" />
                <span className="tray-card-title">{label}</span>
              </button>
              );
            })}
          </div>
        );
      }

      case 'competitionYears': {
        const yearLabels = {
          lt1: trans.competitionYearsLt1,
          '1-2': trans.competitionYears1_2,
          '3-5': trans.competitionYears3_5,
          '5-10': trans.competitionYears5_10,
          '10plus': trans.competitionYears10plus
        };
        return (
          <div className="options-tray animate-slide-up">
            {COMPETITION_YEARS_OPTIONS.map((opt) => {
              const label = yearLabels[opt.band] || opt.band;
              return (
                <button
                  key={opt.band}
                  type="button"
                  onClick={() =>
                    handleSelectOption('competitionYears', opt.band, label, opt)
                  }
                  className="tray-card ripple flex-center"
                >
                  <EmojiEventsIcon className="tray-icon" />
                  <span className="tray-card-title">{label}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case 'style':
        return (
          <div className="options-tray animate-slide-up">
            {playingStyles.map(style => {
              const id = style.style_id;
              const label = getOptionLabel(style);
              return (
              <button
                key={id}
                type="button"
                onClick={() => handleSelectOption('style', id, label, style)}
                className="tray-card ripple flex-center"
              >
                <PsychologyIcon className="tray-icon" />
                <span className="tray-card-title">{label}</span>
              </button>
              );
            })}
          </div>
        );

      case 'primaryFocus':
        const primAttributes = getVisibleFocusAttributes();
        const isLeisureWizard = formData.mainPurposeId === PURPOSE_RECREATIONAL;
        const isHealthWizard = formData.mainPurposeId === PURPOSE_HEALTH;
        return (
          <div className="options-badge-tray animate-slide-up">
            <p className="tray-subtitle">{trans.selectOption}</p>
            <div className="badge-grid">
              {primAttributes.map(attr => {
                const leisureLabel =
                  isLeisureWizard || isHealthWizard
                    ? leisureDisplayLabel(attr.attribute_id, currentLang)
                    : '';
                const label = formatFrenchSentenceCase(
                  leisureLabel || getOptionLabel(attr) || attr.attribute_name,
                  currentLang
                );
                return (
                <button
                  key={attr.attribute_id}
                  type="button"
                  onClick={() => handleSelectOption('primaryFocus', attr.attribute_id, label, attr)}
                  className="badge-button ripple"
                  >
                  {label}
                </button>
                );
              })}
            </div>
          </div>
        );

      case 'secondaryFocus':
        const secAttributes = getVisibleFocusAttributes(formData.primaryFocusId);
        const isCompetitionSecondary =
          Number(smartRef.current.slots?.S?.purposeId) === 3;
        return (
          <SecondaryFocusSelector
            attributes={secAttributes}
            onConfirm={handleConfirmSecondaryFocus}
            trans={trans}
            getLabel={getOptionLabel}
            currentLang={currentLang}
            requireCount={isCompetitionSecondary ? 2 : null}
          />
        );

      case 'country':
        const countriesList = [
          { id: 'LU', name: 'Luxembourg', flag: '🇱🇺' },
          { id: 'DE', name: 'Germany', flag: '🇩🇪' },
          { id: 'FR', name: 'France', flag: '🇫🇷' },
          { id: 'BE', name: 'Belgium', flag: '🇧🇪' },
          { id: 'ES', name: 'Spain', flag: '🇪🇸' },
          { id: 'US', name: 'United States', flag: '🇺🇸' },
          { id: 'CN', name: 'China', flag: '🇨🇳' },
          { id: 'JP', name: 'Japan', flag: '🇯🇵' }
        ];
        const filteredCountries = countriesList.filter(c =>
          c.name.toLowerCase().includes(countrySearch.toLowerCase())
        );

        return (
          <div className="options-badge-tray animate-slide-up">
            <div className="search-input-wrapper">
              <SearchIcon className="search-field-icon" />
              <input
                type="text"
                placeholder={trans.searchCountry}
                value={countrySearch}
                onChange={e => setCountrySearch(e.target.value)}
                className="search-input-field"
              />
            </div>
            <div className="badge-grid">
              {filteredCountries.map(country => (
                <button
                  key={country.id}
                  onClick={() => handleSelectCountry(country.id, `${country.flag} ${country.name}`)}
                  className="badge-button ripple"
                >
                  <span className="flag-span">{country.flag}</span> {country.name}
                </button>
              ))}
            </div>
          </div>
        );

      case 'club':
        const filteredClubs = luxembourgClubs.filter(club =>
          club.name.toLowerCase().includes(clubSearch.toLowerCase()) ||
          club.city.toLowerCase().includes(clubSearch.toLowerCase())
        );

        return (
          <div className="options-badge-tray animate-slide-up">
            <div className="search-input-wrapper">
              <SearchIcon className="search-field-icon" />
              <input
                type="text"
                placeholder={trans.searchClub}
                value={clubSearch}
                onChange={e => setClubSearch(e.target.value)}
                className="search-input-field"
              />
            </div>
            <div className="badge-grid max-height-tray">
              {filteredClubs.slice(0, 15).map(club => (
                <button
                  key={club.id}
                  onClick={() => handleSelectClub(club.id, club.name)}
                  className="badge-button ripple align-left"
                >
                  🏢 {club.name}
                </button>
              ))}
            </div>
          </div>
        );

      case 'level':
        return (
          <div className="options-badge-tray animate-slide-up">
            <p className="tray-subtitle">{trans.selectOption}</p>
            <div className="badge-grid max-height-tray">
              {levels.map(lvl => (
                <button
                  key={lvl.level_id}
                  onClick={() => handleSelectLevel(lvl.level_id, lvl.level_name)}
                  className="badge-button ripple"
                >
                  🏅 {lvl.level_name}
                </button>
              ))}
            </div>
          </div>
        );

      case 'grip':
        const gripOptions = [
          { id: 'shakehand', name: t('shakehand') || 'Shakehand' },
          { id: 'penhold', name: t('penhold') || 'Penhold' },
          { id: 'other', name: t('other') || 'Other' }
        ];
        return (
          <div className="options-tray animate-slide-up">
            {gripOptions.map(g => (
              <button
                key={g.id}
                onClick={() => handleSelectOption('grip', g.id, g.name, g)}
                className="tray-card ripple flex-center"
              >
                <TableTennisRacketIcon className="tray-icon" />
                <span className="tray-card-title">{g.name}</span>
              </button>
            ))}
          </div>
        );

      case 'hand':
        const handOptions = [
          { id: 'right', name: t('rightHand') || 'Right-handed' },
          { id: 'left', name: t('leftHand') || 'Left-handed' }
        ];
        return (
          <div className="options-tray animate-slide-up">
            {handOptions.map(h => (
              <button
                key={h.id}
                onClick={() => handleSelectOption('hand', h.id, h.name, h)}
                className="tray-card ripple flex-center"
              >
                <CheckCircleIcon className="tray-icon" />
                <span className="tray-card-title">{h.name}</span>
              </button>
            ))}
          </div>
        );

      case 'metricTarget':
        // Legacy step removed — metrics come from training analysis (total_stroke_count, max_rally, …).
        // Fall through to weekly volume capture.
      case 'trainingVolume':
        if (isHealthPurpose(formData.mainPurposeId) || isHealthPurpose(smart.slots?.S?.purposeId)) {
          return (
            <div className="options-tray animate-slide-up">
              {HEALTH_SESSIONS.map((id) => {
                const label = trans[`healthSessions_${id}`] || id;
                return (
                  <button
                    key={id}
                    type="button"
                    className="tray-card ripple flex-center"
                    onClick={() =>
                      handleConfirmTrainingVolume({
                        sessionsPerWeek: id,
                        hoursPerSession: 1.5
                      })
                    }
                  >
                    <CalendarMonthIcon className="tray-icon" />
                    <span className="tray-card-title">{label}</span>
                  </button>
                );
              })}
            </div>
          );
        }
        return (
          <TrainingVolumeTray
            trans={trans}
            initial={smart.slots?.M?.weeklyTraining}
            onConfirm={handleConfirmTrainingVolume}
          />
        );

      case 'finalObjective':
        return (
          <FinalObjectiveTray
            trans={trans}
            t={t}
            onConfirm={handleConfirmFinalObjective}
          />
        );

      case 'milestoneTargets': {
        const purposeKey = formData.mainPurposeId === 1 ? 1 : formData.mainPurposeId === 2 ? 2 : 3;
        if (purposeKey === 3) {
          return (
            <CompetitionExpectedResultTray
              trans={trans}
              t={t}
              onConfirm={handleConfirmMilestones}
            />
          );
        }
        const options = (trans.milestoneOptions && trans.milestoneOptions[purposeKey]) || [];
        return (
          <MilestoneTargetsTray
            options={options}
            trans={trans}
            onConfirm={handleConfirmMilestones}
          />
        );
      }

      case 'relevanceWhy':
        return (
          <RelevanceWhyTray
            trans={trans}
            onConfirm={handleConfirmRelevance}
            continueOnly
          />
        );

      case 'healthIntro':
        return (
          <div className="options-tray animate-slide-up">
            <div className="club-admin-health-intro">
              <ScforhApproachIntro variant="compact" />
            </div>
            <button
              type="button"
              className="tray-card ripple flex-center"
              onClick={() => applyAnswer({ type: 'ANSWER_HEALTH_INTRO', payload: {} }, trans.healthContinue)}
            >
              <ArrowForwardIcon className="tray-icon" />
              <span className="tray-card-title">{trans.healthContinue || trans.confirm}</span>
            </button>
          </div>
        );

      case 'healthDimensions':
        return (
          <div className="options-tray animate-slide-up">
            <p className="tray-subtitle">{t('scforhSelectMultiple')}</p>
            <HealthMultiChoice
              options={HEALTH_DIMENSIONS.map((id) => ({
                id,
                label: trans[`healthDim_${id}`] || t(`scforhIntroDim_${id}`)
              }))}
              confirmLabel={trans.confirm}
              onConfirm={(ids, label) =>
                applyAnswer(
                  { type: 'ANSWER_HEALTH_DIMENSIONS', payload: { healthDimensions: ids, label } },
                  label
                )
              }
            />
          </div>
        );

      case 'whoSelfCheck': {
        const band = smart.slots?.health?.whoBand || whoBandFromUserDob(formData.dateOfBirth || user?.user_date_birth);
        return (
          <div className="options-tray animate-slide-up">
            <div className="club-admin-health-intro">
              <ScforhWhoCapsule bands={[band || 'adults']} variant="compact" />
            </div>
            {HEALTH_WHO_SELF_CHECK.map((id) => {
              const label = trans[`healthWho_${id}`] || id;
              return (
                <button
                  key={id}
                  type="button"
                  className="tray-card ripple flex-center"
                  onClick={() =>
                    applyAnswer(
                      {
                        type: 'ANSWER_WHO_SELF_CHECK',
                        payload: { whoSelfCheck: id, whoBand: band, label }
                      },
                      label
                    )
                  }
                >
                  <HealthAndSafetyIcon className="tray-icon" />
                  <span className="tray-card-title">{label}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case 'healthDelivery': {
        const ackLabel =
          trans.qHealthDelivery ||
          'Your coach will make sure the plan is relevant for your objectives.';
        return (
          <div className="options-badge-tray animate-slide-up digiping-relevance-tray">
            <button
              type="button"
              className="tray-confirm-btn ripple"
              onClick={() =>
                applyAnswer(
                  {
                    type: 'ANSWER_HEALTH_DELIVERY',
                    payload: {
                      delivery: 'into_coaching',
                      label: ackLabel
                    }
                  },
                  ackLabel
                )
              }
            >
              {trans.relevanceContinue || trans.confirm || 'Continue'} <ArrowForwardIcon />
            </button>
          </div>
        );
      }

      case 'healthIntensity': {
        const ackLabel =
          trans.qHealthIntensity ||
          'You will have training milestones that support your physical and mental objectives.';
        return (
          <div className="options-badge-tray animate-slide-up digiping-relevance-tray">
            <button
              type="button"
              className="tray-confirm-btn ripple"
              onClick={() =>
                applyAnswer(
                  {
                    type: 'ANSWER_HEALTH_INTENSITY',
                    payload: {
                      intensity: 'regular',
                      label: ackLabel
                    }
                  },
                  ackLabel
                )
              }
            >
              {trans.relevanceContinue || trans.confirm || 'Continue'} <ArrowForwardIcon />
            </button>
          </div>
        );
      }

      case 'healthSnapshot': {
        const preset = resolveHealthSmartPreset({
          seed: formData.healthProfile || smart.slots?.health,
          lang: currentLang
        });
        return (
          <div className="options-tray animate-slide-up leisure-fun-challenge-card">
            <h3 className="tray-card-title">{preset.questTitle}</h3>
            <dl className="leisure-fun-challenge-details">
              <div>
                <dt>{trans.healthSnapshotGoal}</dt>
                <dd>{preset.S.summary}</dd>
              </div>
              <div>
                <dt>{trans.healthSnapshotTrack}</dt>
                <dd>{preset.M.label}</dd>
              </div>
              <div>
                <dt>{trans.healthSnapshotDoable}</dt>
                <dd>{preset.A.intensityNote}</dd>
              </div>
              <div>
                <dt>{trans.healthSnapshotWhy}</dt>
                <dd>{preset.R.whyItMatters}</dd>
              </div>
              <div>
                <dt>{trans.healthSnapshotWhen}</dt>
                <dd>{preset.T.reviewHint}</dd>
              </div>
            </dl>
            <button
              type="button"
              onClick={() =>
                applyAnswer(
                  { type: 'CONFIRM_HEALTH_SNAPSHOT', payload: { lang: currentLang } },
                  trans.healthSnapshotConfirm
                )
              }
              className="tray-card ripple flex-center leisure-fun-challenge-cta"
            >
              <HealthAndSafetyIcon className="tray-icon" />
              <span className="tray-card-title">{trans.healthSnapshotConfirm}</span>
            </button>
          </div>
        );
      }

      case 'funChallengePreview': {
        const preset = resolveLeisureSmartPreset({
          primaryFocusId: smart.slots?.S?.primaryFocusId,
          experienceLevelId: smart.slots?.M?.experienceLevelId,
          dateOfBirth: smart.slots?.R?.dateOfBirth || formData.dateOfBirth,
          lang: currentLang
        });
        return (
          <div className="options-tray animate-slide-up leisure-fun-challenge-card">
            <h3 className="tray-card-title">{preset.questTitle}</h3>
            <dl className="leisure-fun-challenge-details">
              <div>
                <dt>{trans.funChallengeGoal}</dt>
                <dd>{preset.S.summary}</dd>
              </div>
              <div>
                <dt>{trans.funChallengeTrack}</dt>
                <dd>{preset.M.label}</dd>
              </div>
              <div>
                <dt>{trans.funChallengeMilestone}</dt>
                <dd>{preset.A.label}</dd>
              </div>
            </dl>
            <button
              type="button"
              onClick={handleConfirmFunChallenge}
              className="tray-card ripple flex-center leisure-fun-challenge-cta"
            >
              <SportsTennisIcon className="tray-icon" />
              <span className="tray-card-title">{trans.funChallengeConfirm}</span>
            </button>
          </div>
        );
      }

      case 'cycleLength': {
        if (isHealthPurpose(formData.mainPurposeId) || isHealthPurpose(smart.slots?.S?.purposeId)) {
          return null;
        }
        const leisureT =
          isLeisurePurpose(formData.mainPurposeId) ||
          isLeisurePurpose(smart.slots?.S?.purposeId);
        return (
          <div className="options-tray animate-slide-up">
            <p className="tray-subtitle digiping-coach-hint">
              {leisureT
                ? (trans.cycleWeeksLeisureHint || trans.cycleWeeksHint)
                : trans.cycleWeeksHint}
            </p>
            <button
              type="button"
              onClick={() =>
                handleConfirmCycleLength(
                  DEFAULT_TECHNICAL_SPRINT_WEEKS,
                  leisureT
                    ? (trans.cycleWeeksLeisureConfirm || trans.cycleWeeksConfirm || trans.cycleWeeks3)
                    : undefined
                )
              }
              className="tray-card ripple flex-center"
            >
              <CalendarMonthIcon className="tray-icon" />
              <span className="tray-card-title">
                {leisureT
                  ? (trans.cycleWeeksLeisureConfirm || trans.cycleWeeksConfirm || trans.cycleWeeks3)
                  : (trans.cycleWeeksConfirm || trans.cycleWeeks3)}
              </span>
            </button>
          </div>
        );
      }

      case 'gender':
        const genOptions = [
          { id: 'male', name: t('male') || 'Male' },
          { id: 'female', name: t('female') || 'Female' },
          { id: 'other', name: t('other') || 'Other' }
        ];
        return (
          <div className="options-tray animate-slide-up">
            {genOptions.map(g => (
              <button
                key={g.id}
                onClick={() => handleSelectOption('gender', g.id, g.name, g)}
                className="tray-card ripple flex-center"
              >
                <CheckCircleIcon className="tray-icon" />
                <span className="tray-card-title">{g.name}</span>
              </button>
            ))}
          </div>
        );

      case 'customLevel':
      case 'dob':
      case 'firstName':
      case 'lastName':
      case 'name':
      case 'email':
        const placeholderMap = {
          customLevel: trans.enterRank,
          dob: 'DD/MM/YYYY',
          firstName: trans.phFirstName || t('enterFirstName') || 'First name',
          lastName: trans.phLastName || t('enterLastName') || 'Last name',
          name: trans.phFirstName || t('enterFirstName') || 'First name',
          email: 'yourname@example.com'
        };
        const autoCompleteMap = {
          dob: 'bday',
          firstName: 'given-name',
          lastName: 'family-name',
          name: 'given-name',
          email: 'email'
        };
        const iconMap = {
          customLevel: <StarsIcon className="search-field-icon" />,
          dob: <CalendarMonthIcon className="search-field-icon" />,
          firstName: <CheckCircleIcon className="search-field-icon" />,
          lastName: <CheckCircleIcon className="search-field-icon" />,
          name: <CheckCircleIcon className="search-field-icon" />,
          email: <SendIcon className="search-field-icon" />
        };
        return (
          <form onSubmit={handleTextSubmit} className="inline-text-form animate-slide-up">
            <div className="inline-input-wrapper">
              {iconMap[wizardStep]}
              <input
                type="text"
                inputMode={wizardStep === 'dob' ? 'numeric' : undefined}
                autoComplete={autoCompleteMap[wizardStep]}
                maxLength={wizardStep === 'dob' ? 10 : undefined}
                placeholder={placeholderMap[wizardStep]}
                value={textInputValue}
                onChange={handleTextInputChange}
                className="inline-input-field"
                autoFocus
                disabled={emailChecking}
              />
              <button type="submit" className="inline-submit-btn ripple" disabled={emailChecking}>
                <ArrowForwardIcon />
              </button>
            </div>
            {textInputError && <p className="inline-error-text">{textInputError}</p>}
          </form>
        );

      case 'password':
        return (
          <form onSubmit={handlePasswordFormSubmit} className="password-form-tray animate-slide-up">
            <div className="password-fields-row">
              <div className="password-input-group">
                <SecurityIcon className="search-field-icon" />
                <input
                  type="password"
                  placeholder={
                    existingAccount
                      ? trans.passExistingPlaceholder || 'Your password'
                      : trans.passCreatePlaceholder || 'Create Password'
                  }
                  value={passwordForm.password}
                  onChange={e => setPasswordForm(prev => ({ ...prev, password: e.target.value }))}
                  className="inline-input-field"
                  disabled={emailChecking}
                  autoFocus
                />
              </div>
              {!existingAccount ? (
                <div className="password-input-group">
                  <SecurityIcon className="search-field-icon" />
                  <input
                    type="password"
                    placeholder={trans.passConfirmPlaceholder || 'Confirm Password'}
                    value={passwordForm.confirm}
                    onChange={e => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                    className="inline-input-field"
                    disabled={emailChecking}
                  />
                </div>
              ) : null}
            </div>
            <button type="submit" className="password-submit-btn ripple" disabled={emailChecking}>
              {trans.confirm} <ArrowForwardIcon />
            </button>
            {textInputError && <p className="inline-error-text">{textInputError}</p>}
          </form>
        );

      case 'assessment': {
        const isLeisureAssessment = formData.mainPurposeId === 2
          || isLeisurePurpose(smart.slots?.S?.purposeId);
        const mindsetText = formData.mainPurposeId === 3 
          ? trans.competitionMindset 
          : isLeisureAssessment
            ? trans.recreationalMindset 
            : trans.healthMindset;

        if (isLeisureAssessment) {
          const preset = resolveLeisureSmartPreset({
            primaryFocusId: smart.slots?.S?.primaryFocusId,
            experienceLevelId: smart.slots?.M?.experienceLevelId,
            dateOfBirth: smart.slots?.R?.dateOfBirth || formData.dateOfBirth,
            lang: currentLang
          });
          return (
            <div className="assessment-tray assessment-tray--sticky-cta animate-fade-in">
              <div className="assessment-card digiping-snapshot-card leisure-challenge-assessment assessment-card--body">
                <div className="assessment-card-head">
                  <DigipingMark size={36} title="Pingflo" />
                  <h3>{trans.leisureAssessmentTitle || trans.assessmentTitle}</h3>
                </div>
                <dl className="leisure-fun-challenge-details">
                  <div>
                    <dt>{trans.funChallengeGoal}</dt>
                    <dd>{preset.S.summary}</dd>
                  </div>
                  <div>
                    <dt>{trans.funChallengeTrack}</dt>
                    <dd>{preset.M.label}</dd>
                  </div>
                  <div>
                    <dt>{trans.funChallengeMilestone}</dt>
                    <dd>{preset.A.label}</dd>
                  </div>
                </dl>
                {mindsetText ? (
                  <TypewriterText
                    text={mindsetText}
                    className="assessment-body typewriter-text"
                    msPerChar={5}
                  />
                ) : null}
                {assessmentError ? <p className="inline-error-text">{assessmentError}</p> : null}
              </div>
              <button
                onClick={handleCompleteProfiling}
                className="assessment-btn ripple animate-slide-up"
                type="button"
                disabled={isSubmittingProfile}
                aria-busy={isSubmittingProfile}
              >
                {isSubmittingProfile ? (
                  <>{trans.saving}</>
                ) : (
                  <>{trans.letStart} <ArrowForwardIcon /></>
                )}
              </button>
            </div>
          );
        }

        return (
          <DigipingSmartSnapshot
            title={trans.assessmentTitle}
            rows={smartSnapshotRows}
            mindsetText={mindsetText}
            coachBanner={
              smart.coachAgreement?.status === 'pending' ? trans.coachBanner : null
            }
            ctaLabel={
              isSubmittingProfile ? (
                <>{trans.saving}</>
              ) : (
                <>{trans.letStart} <ArrowForwardIcon /></>
              )
            }
            onConfirm={handleCompleteProfiling}
            DigipingMark={DigipingMark}
            TypewriterText={TypewriterText}
            RichText={RichText}
            submitting={isSubmittingProfile}
            errorText={assessmentError || null}
          />
        );
      }

      default:
        return null;
    }
  };

  const joinClubName = joinClub?.club_name || invitationData?.club_name || null;
  const joinLogoSrc =
    resolveClubImageUrl(joinClub?.club_image) ||
    resolveClubImageUrl(brand?.logo_url) ||
    null;
  const showJoinBanner = Boolean(
    joinClubName && (invitationData?.club_id || joinClub?.club_id || paramClubCode)
  );
  const joinBannerText = (trans.joinClubBanner || 'Join {club}').replace(
    '{club}',
    joinClubName || ''
  );

  // Rendering the SMART intro overlay card (letter-stream like Pingflo chat)
  if (!introStarted) {
    const isLeisureIntro =
      !isPublicClubJoin &&
      (isLeisurePurpose(smart.slots?.S?.purposeId) ||
        isLeisurePurpose(formData.mainPurposeId) ||
        Number(formData.mainPurposeId) === PURPOSE_RECREATIONAL);
    const storedUser = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || 'null');
      } catch {
        return null;
      }
    })();
    const isHealthIntro =
      isHealthPurpose(smart.slots?.S?.purposeId) ||
      isHealthPurpose(formData.mainPurposeId) ||
      (!isPublicClubJoin &&
        Number(storedUser?.main_purpose_id) === PURPOSE_HEALTH &&
        storedUser?.user_id);
    const smartItems = isHealthIntro
      ? (trans.healthSmartItems || trans.smartItems || [])
      : isLeisureIntro
        ? (trans.leisureSmartItems || [])
        : (trans.smartItems || []);
    const introTitle = isHealthIntro
      ? (trans.healthIntroTitle || trans.introTitle)
      : isLeisureIntro
        ? (trans.leisureIntroTitle || trans.introTitle)
        : trans.introTitle;
    const introText = isHealthIntro
      ? (trans.healthIntroText || trans.introText)
      : isLeisureIntro
        ? (trans.leisureIntroText || trans.introText)
        : trans.introText;
    const introAction = isHealthIntro
      ? (trans.healthIntroAction || trans.introAction)
      : isLeisureIntro
        ? (trans.leisureIntroAction || trans.introAction)
        : trans.introAction;
    const startLabel = isHealthIntro
      ? (trans.healthStartBtn || trans.startBtn)
      : isLeisureIntro
        ? (trans.leisureStartBtn || trans.startBtn)
        : (pendingResume ? (trans.continueBtn || trans.startBtn) : trans.startBtn);
    const showSmart = introPhase !== 'body';
    const showAction = introPhase === 'action' || introPhase === 'ready';
    const introReady = introPhase === 'ready';

    return (
      <div className="wizard-full-bg">
        <div className="wizard-intro-card animate-fade-in">
          <div className="wizard-intro-card__body">
          <div className="wizard-intro-header">
            <div className="logo-badge logo-badge--digiping">
              {joinLogoSrc ? (
                <img src={joinLogoSrc} alt={joinClubName || 'Club'} className="wizard-join-logo" />
              ) : (
                <DigipingMark size={44} title="Pingflo" />
              )}
            </div>
            <div className="wizard-intro-titles">
              <div className="wizard-join-row">
                {showJoinBanner ? (
                  <span className="wizard-join-banner">{joinBannerText}</span>
                ) : null}
                <LanguageShortcutFab
                  variant="invite"
                  onPicked={markInviteUiLangOverride}
                />
              </div>
              <span className="digiping-tag">{trans.digipingTag}</span>
              <h2>{introTitle}</h2>
            </div>
          </div>

          {introPhase === 'body' ? (
            <TypewriterText
              text={introText}
              className="intro-body-text"
              msPerChar={9}
              onComplete={() => {
                setIntroSmartIdx(0);
                setIntroPhase('smart');
              }}
            />
          ) : (
            <RichText text={trans.introText} className="intro-body-text" />
          )}

          {showSmart ? (
            <div className="smart-grid" aria-live="polite">
              {smartItems.map((item, i) => {
                const revealed = introPhase !== 'smart' || i < introSmartIdx;
                const streaming = introPhase === 'smart' && i === introSmartIdx;
                if (!revealed && !streaming) return null;
                return (
                  <div
                    key={item.letter}
                    className={`smart-bullet smart-bullet--${item.letter.toLowerCase()}${streaming ? ' smart-bullet--streaming' : ''}`}
                  >
                    <span className="smart-letter" aria-hidden="true">{item.letter}</span>
                    <div className="smart-bullet-copy">
                      <span className={`smart-label dp-em dp-em--${item.letter.toLowerCase()}`}>
                        {item.label}
                      </span>
                      {revealed ? (
                        <span className="smart-desc">{item.desc}</span>
                      ) : (
                        <TypewriterText
                          text={item.desc}
                          className="smart-desc"
                          msPerChar={7}
                          richWhileStreaming={false}
                          onComplete={() => {
                            if (i + 1 >= smartItems.length) {
                              setIntroPhase('action');
                            } else {
                              setIntroSmartIdx(i + 1);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {showAction ? (
            introPhase === 'action' ? (
              <TypewriterText
                text={trans.introAction}
                className="intro-action-prompt"
                msPerChar={9}
                onComplete={() => setIntroPhase('ready')}
              />
            ) : (
              <p className="intro-action-prompt">{trans.introAction}</p>
            )
          ) : null}

          {pendingResume && introReady ? (
            <p className="intro-resume-hint">{trans.resumeHint}</p>
          ) : null}

          </div>
          <div className="wizard-intro-card__cta">
          {introReady ? (
            <>
              <button
                onClick={pendingResume ? resumeWizard : startWizard}
                className="wizard-begin-btn ripple"
                disabled={Boolean(paramClubCode) && !invitationData?.club_id && !joinClub?.club_id}
              >
                {pendingResume ? trans.continueBtn : trans.startBtn} <ArrowForwardIcon />
              </button>
              {pendingResume && (
                <button
                  type="button"
                  onClick={startWizard}
                  className="wizard-begin-btn wizard-begin-btn--secondary ripple"
                  disabled={Boolean(paramClubCode) && !invitationData?.club_id && !joinClub?.club_id}
                >
                  {trans.startBtn}
                </button>
              )}
            </>
          ) : (
            <div className="intro-stream-status" aria-live="polite">
              <span className="typing-indicator-dots">
                <span /><span /><span />
              </span>
              <span className="intro-stream-status__label">{trans.typing}</span>
            </div>
          )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-full-bg wizard-full-bg--chat">
      <div className="wizard-chat-container wizard-chat-container--fill">
        <div className="wizard-chat-header wizard-chat-header--compact">
          <div className="wizard-chat-header__brand">
            <div className="coach-avatar coach-avatar--header" title={joinClubName || "Pingflo"}>
              {joinLogoSrc ? (
                <img src={joinLogoSrc} alt={joinClubName || 'Club'} className="wizard-join-logo wizard-join-logo--sm" />
              ) : (
                <DigipingMark size={22} title="Pingflo" />
              )}
            </div>
            <div className="wizard-chat-header__text">
              <span className="coach-label">{trans.coachName}</span>
              <span className="digiping-tag">{trans.digipingTag}</span>
              {showJoinBanner && (
                <span className="wizard-join-banner wizard-join-banner--compact">{joinBannerText}</span>
              )}
            </div>
            <LanguageShortcutFab
              variant="invite"
              onPicked={markInviteUiLangOverride}
            />
          </div>
        </div>
        
        {/* Chat Stream Area */}
        <div className="wizard-chat-history" ref={chatHistoryRef}>
          {messages.map((msg, index) => {
            const isSystem = msg.isSystem;
            const bubbleClass = msg.sender === 'ai' ? 'ai-bubble' : 'user-bubble';
            const wrapperClass = isSystem ? 'system-row' : msg.sender === 'ai' ? 'ai-row' : 'user-row';

            if (isSystem) {
              return (
                <div key={msg.id || index} className={wrapperClass} data-msg-id={msg.id}>
                  <div className="system-msg-box animate-scale-up">
                    <RichText text={msg.text} />
                  </div>
                </div>
              );
            }

            const isStreaming = msg.sender === 'ai' && msg.id === streamingMsgId;
            const dim = msg.dimension || smart.activeDimension;

            return (
              <div
                key={msg.id || index}
                className={`${wrapperClass} animate-fade-in`}
                data-msg-id={msg.id}
                data-dimension={msg.dimension || undefined}
              >
                {msg.sender === 'ai' && (
                  <div className="coach-avatar" title="Pingflo">
                    <DigipingMark size={30} title="Pingflo" />
                  </div>
                )}
                <div className={`${bubbleClass} glass-item`}>
                  {msg.sender === 'ai' && (
                    <div className="coach-label-row">
                      <span className="coach-label">{trans.coachName}</span>
                      {dim && dimensionLabels[dim] && (
                        <span className={`dimension-chip dp-em dp-em--${String(dim).toLowerCase()}`}>
                          {dimensionLabels[dim].letter} · {dimensionLabels[dim].label}
                        </span>
                      )}
                    </div>
                  )}
                  {isStreaming ? (
                    <TypewriterText
                      text={msg.text}
                      onTick={() => scrollQuestionIntoView('auto')}
                      onComplete={() => setStreamingMsgId(null)}
                    />
                  ) : msg.sender === 'ai' ? (
                    <RichText text={msg.text} className="msg-content-text" />
                  ) : (
                    <div className="user-answer-row">
                      <p className="msg-content-text">{msg.text}</p>
                      {msg.step && !isTyping && !streamingMsgId && (
                        <button
                          type="button"
                          className="edit-answer-btn"
                          title={trans.editAnswer}
                          aria-label={trans.editAnswer}
                          onClick={() => beginEditAnswer(msg.id, msg.step)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                          <span>{trans.editAnswer}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Typing indicator */}
          {isTyping && (
            <div className="ai-row animate-fade-in">
              <div className="coach-avatar" title="Pingflo">
                <DigipingMark size={30} title="Pingflo" />
              </div>
              <div className="ai-bubble glass-item typing-bubble">
                <div className="coach-label-row">
                  <span className="coach-label">{trans.coachName}</span>
                  {(STEP_TO_DIMENSION[wizardStep] || smart.activeDimension) &&
                    dimensionLabels[STEP_TO_DIMENSION[wizardStep] || smart.activeDimension] && (
                    <span className={`dimension-chip dp-em dp-em--${(STEP_TO_DIMENSION[wizardStep] || smart.activeDimension).toLowerCase()}`}>
                      {dimensionLabels[STEP_TO_DIMENSION[wizardStep] || smart.activeDimension].letter} · {dimensionLabels[STEP_TO_DIMENSION[wizardStep] || smart.activeDimension].label}
                    </span>
                  )}
                </div>
                <div className="typing-indicator-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={chatEndRef} className="wizard-chat-history__spacer" aria-hidden="true" />
        </div>

        {/* Input/Interactive Options Tray */}
        <div
          className={`wizard-interactive-footer${
            wizardStep === 'assessment' ? ' wizard-interactive-footer--assessment' : ''
          }`}
        >
          {editingStep && editingStep === wizardStep && !isTyping && !streamingMsgId && (
            <p className="editing-hint">{trans.editingHint}</p>
          )}
          {renderInteractiveTray()}
        </div>

      </div>
    </div>
  );
};

/** Letter-by-letter reveal to mimic streamed AI chat replies. */
const TypewriterText = ({
  text,
  onComplete,
  onTick,
  msPerChar = 11,
  className = 'msg-content-text typewriter-text',
  richWhileStreaming = true
}) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const completedRef = useRef(false);

  useEffect(() => {
    completedRef.current = false;
    setVisibleCount(0);
    if (!text) {
      completedRef.current = true;
      onComplete?.();
      return undefined;
    }

    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setVisibleCount(i);
      onTick?.();
      if (i >= text.length) {
        clearInterval(id);
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
      }
    }, msPerChar);

    return () => clearInterval(id);
    // Restart only when the message text changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const full = String(text || '');
  const done = visibleCount >= full.length && full;

  if (done) {
    return <RichText text={full} className={className} />;
  }

  if (richWhileStreaming) {
    return (
      <div className={`${className} typewriter-streaming`.trim()}>
        <RichText text={safeMarkdownSlice(full, visibleCount)} />
        {visibleCount < full.length ? (
          <span className="typewriter-caret" aria-hidden="true" />
        ) : null}
      </div>
    );
  }

  return (
    <p className={className}>
      {visiblePlainSlice(full, visibleCount)}
      {visibleCount < full.length ? (
        <span className="typewriter-caret" aria-hidden="true" />
      ) : null}
    </p>
  );
};

const MetricTargetTray = ({ options = [], trans, onConfirm }) => {
  const [selectedId, setSelectedId] = useState(options[0]?.id || 'custom');
  const [targetValue, setTargetValue] = useState('');
  const [customLabel, setCustomLabel] = useState('');

  const selected = options.find((o) => o.id === selectedId);
  const isCustom = selectedId === 'custom' || !selected;

  const handleSubmit = () => {
    const unit = selected?.unit || '';
    const kind = selected?.kind || 'custom';
    const label = isCustom
      ? (customLabel.trim() || trans.metricCustomLabel)
      : selected.label;
    const num = targetValue.trim() === '' ? null : Number(targetValue);
    onConfirm({
      kind,
      targetValue: Number.isFinite(num) ? num : targetValue.trim() || null,
      unit,
      label
    });
  };

  const canSubmit =
    targetValue.trim() !== '' && (isCustom ? customLabel.trim().length > 0 : true);

  return (
    <div className="options-badge-tray animate-slide-up digiping-metric-tray">
      <p className="tray-subtitle">{trans.selectOption}</p>
      <div className="badge-grid max-height-tray">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setSelectedId(opt.id)}
            className={`badge-button ripple ${selectedId === opt.id ? 'active-badge' : ''}`}
          >
            {opt.label}
            {opt.unit ? ` (${opt.unit})` : ''}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setSelectedId('custom')}
          className={`badge-button ripple ${isCustom ? 'active-badge' : ''}`}
        >
          {trans.metricCustomLabel}
        </button>
      </div>
      {isCustom && (
        <input
          type="text"
          className="inline-input-field digiping-tray-input"
          placeholder={trans.metricCustomLabel}
          value={customLabel}
          onChange={(e) => setCustomLabel(e.target.value)}
        />
      )}
      <div className="digiping-metric-target-row">
        <input
          type="number"
          inputMode="decimal"
          className="inline-input-field digiping-tray-input"
          placeholder={trans.metricTargetPlaceholder}
          value={targetValue}
          onChange={(e) => setTargetValue(e.target.value)}
        />
        {selected?.unit ? <span className="digiping-metric-unit">{selected.unit}</span> : null}
      </div>
      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="tray-confirm-btn ripple"
      >
        {trans.confirm} <ArrowForwardIcon />
      </button>
    </div>
  );
};

const FinalObjectiveTray = ({ trans, t, onConfirm }) => {
  const [kind, setKind] = useState('classement');
  const [customText, setCustomText] = useState('');
  const optionLabels = {
    classement: trans.finalObjectiveKindClassement,
    team_championship: trans.finalObjectiveKindTeam,
    tournament: trans.finalObjectiveKindTournament,
    other: trans.finalObjectiveKindOther
  };
  const label = composeFinalObjectiveLabel({
    kind,
    customText,
    optionLabels,
    t
  });
  const canSubmit = kind === 'other' ? Boolean(customText.trim()) : Boolean(kind && label);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm({
      kind,
      kinds: [kind],
      customText: kind === 'other' ? customText.trim() : '',
      label,
      resourceNote: ''
    });
  };

  return (
    <div className="options-badge-tray animate-slide-up digiping-milestone-tray">
      <p className="tray-subtitle digiping-coach-hint">
        {trans.finalObjectiveHint}
      </p>
      <div className="badge-grid max-height-tray">
        {COMPETITION_OBJECTIVE_KINDS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setKind(opt.id)}
            className={`badge-button ripple digiping-milestone-chip ${kind === opt.id ? 'active-badge' : ''}`}
          >
            {kind === opt.id ? '✅ ' : ''}
            {optionLabels[opt.id]}
          </button>
        ))}
      </div>
      {kind === 'other' ? (
        <input
          type="text"
          className="inline-input-field digiping-tray-input"
          placeholder={trans.finalObjectiveOtherPlaceholder}
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
        />
      ) : null}
      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="tray-confirm-btn ripple"
      >
        {trans.confirm} <ArrowForwardIcon />
      </button>
    </div>
  );
};

const CompetitionExpectedResultTray = ({ trans, t, onConfirm }) => {
  const [kind, setKind] = useState('classement_step');
  const [targetValue, setTargetValue] = useState('');
  const [resourceNote, setResourceNote] = useState('');
  const options = useMemo(() => competitionExpectedOptions(t), [t]);

  const handleSubmit = () => {
    const opt = options.find((o) => o.id === kind) || options[0];
    const target = { value: targetValue.trim() || undefined };
    const label = buildExpectedResultLabel({ kind, target, t });
    const milestones = [
      {
        id: kind,
        kind,
        expectedResultKind: kind,
        horizon: 'mid',
        label,
        target,
      },
    ];
    onConfirm({ milestones, resourceNote: resourceNote.trim() });
  };

  const needsValue = kind !== 'match_target';

  return (
    <div className="options-badge-tray animate-slide-up digiping-milestone-tray">
      <p className="tray-subtitle digiping-coach-hint">
        {trans.competitionExpectedHint || trans.milestonesHint}
      </p>
      <div className="badge-grid max-height-tray">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setKind(opt.id)}
            className={`badge-button ripple digiping-milestone-chip ${kind === opt.id ? 'active-badge' : ''}`}
          >
            {kind === opt.id ? '✅ ' : ''}
            {opt.label}
          </button>
        ))}
      </div>
      {needsValue ? (
        <input
          type="text"
          className="inline-input-field digiping-tray-input"
          placeholder={t('smartExpectedResultTargetPlaceholder', { defaultValue: 'Target value (e.g. 1200 points, D3…)' })}
          value={targetValue}
          onChange={(e) => setTargetValue(e.target.value)}
        />
      ) : null}
      <input
        type="text"
        className="inline-input-field digiping-tray-input"
        placeholder={trans.resourceNotePlaceholder}
        value={resourceNote}
        onChange={(e) => setResourceNote(e.target.value)}
      />
      <button type="button" onClick={handleSubmit} className="tray-confirm-btn ripple">
        {trans.confirm} <ArrowForwardIcon />
      </button>
    </div>
  );
};

const MilestoneTargetsTray = ({ options = [], trans, onConfirm }) => {
  const [selected, setSelected] = useState([]);
  const [horizons, setHorizons] = useState({});
  const [resourceNote, setResourceNote] = useState('');

  const toggle = (opt) => {
    setSelected((prev) => {
      if (prev.includes(opt.id)) return prev.filter((id) => id !== opt.id);
      if (prev.length >= 2) return prev;
      setHorizons((h) => ({ ...h, [opt.id]: h[opt.id] || opt.horizon || 'mid' }));
      return [...prev, opt.id];
    });
  };

  const cycleHorizon = (id, e) => {
    e.stopPropagation();
    setHorizons((h) => ({
      ...h,
      [id]: h[id] === 'long' ? 'mid' : 'long'
    }));
  };

  const handleSubmit = () => {
    const milestones = selected.map((id) => {
      const opt = options.find((o) => o.id === id);
      return {
        id,
        horizon: horizons[id] || opt?.horizon || 'mid',
        label: opt?.label || id,
        challenge: ''
      };
    });
    onConfirm({ milestones, resourceNote: resourceNote.trim() });
  };

  return (
    <div className="options-badge-tray animate-slide-up digiping-milestone-tray">
      <p className="tray-subtitle digiping-coach-hint">{trans.milestonesHint}</p>
      <div className="badge-grid max-height-tray">
        {options.map((opt) => {
          const isOn = selected.includes(opt.id);
          const hz = horizons[opt.id] || opt.horizon || 'mid';
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt)}
              className={`badge-button ripple digiping-milestone-chip ${isOn ? 'active-badge' : ''}`}
            >
              <span>{isOn ? '✅ ' : ''}{opt.label}</span>
              {isOn && (
                <span
                  role="button"
                  tabIndex={0}
                  className="digiping-horizon-tag"
                  onClick={(e) => cycleHorizon(opt.id, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') cycleHorizon(opt.id, e);
                  }}
                >
                  {hz === 'long' ? trans.horizonLong : trans.horizonMid}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <input
        type="text"
        className="inline-input-field digiping-tray-input"
        placeholder={trans.resourceNotePlaceholder}
        value={resourceNote}
        onChange={(e) => setResourceNote(e.target.value)}
      />
      <button
        type="button"
        disabled={selected.length < 1}
        onClick={handleSubmit}
        className="tray-confirm-btn ripple"
      >
        {trans.confirm} ({selected.length}/2) <ArrowForwardIcon />
      </button>
    </div>
  );
};

const RelevanceWhyTray = ({ chips = [], trans, onConfirm, continueOnly = false }) => {
  const [picked, setPicked] = useState([]);
  const [custom, setCustom] = useState('');

  const toggle = (id) => {
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleContinue = () => {
    const ack =
      trans.relevanceAckLabel ||
      'Coach will validate and align training';
    onConfirm(ack);
  };

  const handleSubmit = () => {
    const chipLabels = chips.filter((c) => picked.includes(c.id)).map((c) => c.label);
    const why = [...chipLabels, custom.trim()].filter(Boolean).join(' · ');
    if (!why) return;
    onConfirm(why);
  };

  if (continueOnly) {
    return (
      <div className="options-badge-tray animate-slide-up digiping-relevance-tray">
        <p className="tray-subtitle digiping-coach-hint">{trans.coachBanner}</p>
        <button
          type="button"
          onClick={handleContinue}
          className="tray-confirm-btn ripple"
        >
          {trans.relevanceContinue || trans.confirm || 'Continue'} <ArrowForwardIcon />
        </button>
      </div>
    );
  }

  const canSubmit = picked.length > 0 || custom.trim().length > 0;

  return (
    <div className="options-badge-tray animate-slide-up digiping-relevance-tray">
      <p className="tray-subtitle digiping-coach-hint">{trans.coachBanner}</p>
      <div className="badge-grid max-height-tray">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => toggle(chip.id)}
            className={`badge-button ripple ${picked.includes(chip.id) ? 'active-badge' : ''}`}
          >
            {picked.includes(chip.id) ? '✅ ' : ''}
            {chip.label}
          </button>
        ))}
      </div>
      <input
        type="text"
        className="inline-input-field digiping-tray-input"
        placeholder={trans.relevanceCustomPlaceholder}
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="tray-confirm-btn ripple"
      >
        {trans.confirm} <ArrowForwardIcon />
      </button>
    </div>
  );
};

const SESSION_OPTIONS = [
  { id: '1', label: '1' },
  { id: '2-3', label: '2-3' },
  { id: '4-5', label: '4-5' },
  { id: '5+', label: '5+' },
];

const HOUR_PER_SESSION_OPTIONS = [
  { value: 1, label: '1h' },
  { value: 1.5, label: '1.5h' },
  { value: 2, label: '2h' },
  { value: 2.5, label: '2h30' },
  { value: 3, label: '3h' },
];

/** WHO adult guideline: ≥150 minutes moderate activity per week. */
const WHO_WEEKLY_ACTIVITY_MINUTES = 150;

function estimateSessionsPerWeek(band) {
  switch (String(band)) {
    case '1':
      return 1;
    case '2':
      return 2;
    case '3+':
      return 3;
    case '2-3':
      return 2.5;
    case '4-5':
      return 4.5;
    case '5+':
      return 5;
    default: {
      const n = Number(band);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }
  }
}

function estimateWeeklyTrainingMinutes(sessionsPerWeek, hoursPerSession) {
  const sessions = estimateSessionsPerWeek(sessionsPerWeek);
  const hours = Number(hoursPerSession) || 0;
  return Math.round(sessions * hours * 60);
}

function formatHoursPerSessionLabel(hours, unitSuffix = 'h/session') {
  const opt = HOUR_PER_SESSION_OPTIONS.find((o) => o.value === Number(hours));
  if (opt) return `${opt.label} (${unitSuffix})`;
  return `${hours}h (${unitSuffix})`;
}

const TrainingVolumeTray = ({ trans, initial, onConfirm }) => {
  const [sessions, setSessions] = useState(
    initial?.sessionsPerWeek != null ? String(initial.sessionsPerWeek) : null
  );
  const [hoursPerSession, setHoursPerSession] = useState(
    initial?.hoursPerSession != null
      ? Number(initial.hoursPerSession)
      : null
  );

  const canSubmit = sessions != null && hoursPerSession != null;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm({
      sessionsPerWeek: sessions,
      hoursPerSession
    });
  };

  return (
    <div className="options-badge-tray animate-slide-up digiping-training-volume-tray">
      <p className="tray-subtitle">{trans.sessionsPerWeekLabel}</p>
      <div className="badge-grid">
        {SESSION_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setSessions(opt.id)}
            className={`badge-button ripple ${sessions === opt.id ? 'active-badge' : ''}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <p className="tray-subtitle">
        {trans.hoursPerSessionLabel || trans.hoursPerWeekLabel}
      </p>
      <div className="badge-grid">
        {HOUR_PER_SESSION_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setHoursPerSession(opt.value)}
            className={`badge-button ripple ${
              hoursPerSession === opt.value ? 'active-badge' : ''
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="tray-confirm-btn ripple"
      >
        {trans.confirm} <ArrowForwardIcon />
      </button>
    </div>
  );
};

const SecondaryFocusSelector = ({
  attributes,
  onConfirm,
  trans,
  getLabel,
  currentLang,
  requireCount = null,
}) => {
  const [selected, setSelected] = useState([]);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const canSubmit =
    requireCount == null ? true : selected.length === Number(requireCount);

  const toggleSelect = (id) => {
    if (selected.includes(id)) {
      setSelected(prev => prev.filter(item => item !== id));
    } else {
      if (selected.length >= 2) return; // max 2 secondary focuses
      setSelected(prev => [...prev, id]);
    }
  };

  const handleSubmit = useCallback(() => {
    if (requireCount != null && selectedRef.current.length !== Number(requireCount)) {
      return;
    }
    onConfirm(selectedRef.current);
  }, [onConfirm, requireCount]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Enter' || event.repeat || event.isComposing) return;
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag === 'textarea' || tag === 'select') return;
      if (tag === 'input') {
        const type = String(event.target?.type || '').toLowerCase();
        if (type !== 'button' && type !== 'submit' && type !== 'checkbox' && type !== 'radio') {
          return;
        }
      }
      if (requireCount != null && selectedRef.current.length !== Number(requireCount)) {
        return;
      }
      event.preventDefault();
      handleSubmit();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [handleSubmit, requireCount]);

  return (
    <div className="options-badge-tray options-badge-tray--sticky-confirm animate-slide-up">
      <p className="tray-subtitle">{trans.selectOption}</p>
      <div className="badge-grid" role="group" aria-label={trans.qSecondaryFocus}>
        {attributes.map(attr => {
          const isSelected = selected.includes(attr.attribute_id);
          const label = (getLabel ? getLabel(attr) : null) || formatFrenchSentenceCase(attr.attribute_name, currentLang);
          return (
            <button
              key={attr.attribute_id}
              type="button"
              onClick={() => toggleSelect(attr.attribute_id)}
              className={`badge-button ripple ${isSelected ? 'active-badge' : ''}`}
              aria-pressed={isSelected}
            >
              {isSelected ? '✅ ' : ''} {label}
            </button>
          );
        })}
      </div>
      <button
        onClick={handleSubmit}
        className="tray-confirm-btn ripple"
        type="button"
        disabled={!canSubmit}
        aria-keyshortcuts="Enter"
      >
        {trans.confirm} ({selected.length}/2) <ArrowForwardIcon />
      </button>
      <p className="tray-confirm-hint">
        {trans.enterToContinue || 'Press'} <kbd>Enter</kbd>
      </p>
    </div>
  );
};

function HealthMultiChoice({ options, confirmLabel, onConfirm }) {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const label = options.filter((o) => selected.includes(o.id)).map((o) => o.label).join(', ');
  return (
    <div className="health-multi-choice">
      <div className="badge-grid">
        {options.map((opt) => {
          const on = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              className={`badge-button ripple ${on ? 'active-badge' : ''}`}
              onClick={() => toggle(opt.id)}
              aria-pressed={on}
            >
              {on ? '✓ ' : ''}
              {opt.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="tray-confirm-btn ripple"
        disabled={!selected.length}
        onClick={() => onConfirm(selected, label)}
      >
        {confirmLabel} <ArrowForwardIcon />
      </button>
    </div>
  );
}

export default AIProfilingWizard;
